/**
 * OBS Service for direct file upload/download operations.
 * Supports direct OBS upload and backend proxy fallback for external users.
 */

import {
  getUploadUrl,
  getDownloadUrl,
  initMultipartUpload,
  refreshMultipartUrls,
  completeMultipartUpload,
  proxyUpload,
  proxyUploadPart,
  notifyUploadComplete,
  reportUploadEvent,
  verifyFile,
} from '@/api/storage';
import type { UploadConfig } from '@/types';
import SparkMD5 from 'spark-md5';
import { calculateFileMD5 } from '@/utils';
import { uploadLog, uploadWarn, uploadError } from '@/utils/uploadLogger';
import { useAuthStore } from '@/store/authStore';

/**
 * ✅ Upload task management for supporting upload cancellation
 */
interface UploadTask {
  file: File;
  xhr?: XMLHttpRequest;
  controller?: AbortController;
  aborted: boolean;
}

const activeUploads = new Map<string, UploadTask>();
// Keys that have been explicitly cancelled by the user — persists across retry attempts
const cancelledUploadKeys = new Set<string>();

/**
 * Generate unique upload ID
 */
const generateUploadId = (file: File): string => {
  return `${file.name}_${file.size}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // Use multipart for files > 100MB
const PART_UPLOAD_TIMEOUT = 300_000; // 300s (5 min) timeout per part for large files
const UPLOAD_STALL_TIMEOUT_MS = 30_000; // abort & retry if no upload progress for 30s
const MAX_PART_RETRIES = 3;
const OBS_DIRECT_FAILED_KEY = 'obs_direct_failed';
// 探测只为提前得知 OBS 是否可达，不阻塞任何上传；超时过长会让首屏长时间占用
// 一个连接与一次后端签名请求，故取 5s——可达时通常 <1s 返回，不可达时快速失败。
const OBS_PROBE_TIMEOUT = 5_000;
const UPLOAD_RESUME_KEY_PREFIX = 'upload_resume_'; // Prefix for resume data in localStorage
const OBS_PROBE_CACHE_KEY = 'obs_probe_result'; // Cache key for probe result
// 成功结果缓存 10 分钟；失败结果缓存更久，避免 OBS 不可达时每 10 分钟重试一轮拖慢页面。
const OBS_PROBE_CACHE_TTL = 10 * 60 * 1000;
const OBS_PROBE_FAILURE_CACHE_TTL = 30 * 60 * 1000;

/**
 * Determine chunk size based on file size.
 * < 10GB  → 1MB
 * 10–20GB → 2MB
 * 20–50GB → 5MB
 * 50GB+   → 10MB
 */
const getChunkSizeByFileSize = (fileSize: number): number => {
  const GB = 1024 * 1024 * 1024;
  const MB = 1024 * 1024;
  if (fileSize < 10 * GB) return 1 * MB;
  if (fileSize < 20 * GB) return 2 * MB;
  if (fileSize < 50 * GB) return 5 * MB;
  return 10 * MB;
};

// Concurrent upload configuration
const DEFAULT_MAX_CONCURRENT_UPLOADS = 2; // Optimized default for better performance
const MAX_MAX_CONCURRENT_UPLOADS = 8; // Safety upper limit (increased for high-bandwidth users)
const OBS_MAX_CONCURRENT_KEY = 'obs_max_concurrent_uploads';

/**
 * Get configured max concurrent uploads from localStorage
 * Falls back to safe default if not configured or invalid
 */
const getMaxConcurrentUploads = (): number => {
  try {
    const configured = parseInt(localStorage.getItem(OBS_MAX_CONCURRENT_KEY) || '0');
    if (configured > 0 && configured <= MAX_MAX_CONCURRENT_UPLOADS) {
      uploadLog(`[Upload:Config] Using configured concurrency: ${configured}`);
      return configured;
    }
  } catch (e) {
    uploadWarn('[Upload:Config] Failed to read concurrency config:', e);
  }
  uploadLog(`[Upload:Config] Using default concurrency: ${DEFAULT_MAX_CONCURRENT_UPLOADS}`);
  return DEFAULT_MAX_CONCURRENT_UPLOADS;
};

const MAX_CONCURRENT_UPLOADS = getMaxConcurrentUploads();

// Batch progress saving configuration
const SAVE_PROGRESS_BATCH_SIZE = 5; // Save every 5 parts to reduce localStorage overhead

// Retry configuration for Proxy mode uploads
const MAX_PROXY_PART_RETRIES = 3;  // Maximum retry attempts for each part in Proxy mode

// Async mutex for synchronizing shared state updates
class AsyncMutex {
  private queue: Array<() => void> = [];
  private locked = false;

  async lock(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.locked) {
        this.locked = true;
        resolve();
      } else {
        this.queue.push(resolve);
      }
    });
  }

  unlock(): void {
    const nextResolve = this.queue.shift();
    if (nextResolve) {
      nextResolve();
    } else {
      this.locked = false;
    }
  }
}

// Network speed test result interface
export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
  speed?: number; // bytes per second (average speed)
  currentSpeed?: number; // bytes per second (real-time speed, recent 5s)
  remainingTime?: number; // seconds
  status?: 'uploading' | 'merging';
}

export interface UploadResult {
  objectKey: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  md5: string;  // MD5 hash value (hex format)
}

interface UploadResumeData {
  uploadId: string;
  objectKey: string;
  fileName: string;
  fileSize: number;
  completedParts: Array<{ part_number: number; etag: string }>;
  timestamp: number;
  chunkSize: number; // Track chunk size to detect configuration changes
}

/**
 * Generate a unique key for a file based on its properties.
 */
const getFileKey = (file: File): string => {
  return `${file.name}_${file.size}_${file.lastModified}`;
};

/**
 * Save upload progress to localStorage for resume capability.
 */
const saveUploadProgress = (
  fileKey: string,
  uploadId: string,
  objectKey: string,
  fileName: string,
  fileSize: number,
  completedParts: Array<{ part_number: number; etag: string }>,
  chunkSize: number
): void => {
  try {
    const data: UploadResumeData = {
      uploadId,
      objectKey,
      fileName,
      fileSize,
      completedParts,
      timestamp: Date.now(),
      chunkSize,
    };
    localStorage.setItem(UPLOAD_RESUME_KEY_PREFIX + fileKey, JSON.stringify(data));
  } catch (err) {
    uploadWarn('Failed to save upload progress:', err);
  }
};

/**
 * Load upload progress from localStorage.
 */
const loadUploadProgress = (fileKey: string): UploadResumeData | null => {
  try {
    const data = localStorage.getItem(UPLOAD_RESUME_KEY_PREFIX + fileKey);
    if (!data) return null;

    const resumeData: UploadResumeData = JSON.parse(data);

    // Check if resume data is too old (older than 23 hours, considering 24h URL expiry)
    const MAX_RESUME_AGE = 23 * 60 * 60 * 1000; // 23 hours
    if (Date.now() - resumeData.timestamp > MAX_RESUME_AGE) {
      uploadLog('[Upload:Resume] Resume data expired, clearing...');
      clearUploadProgress(fileKey);
      return null;
    }

    if (!resumeData.chunkSize) {
      uploadWarn('[Upload:Resume] Resume data missing chunkSize, discarding');
      clearUploadProgress(fileKey);
      return null;
    }

    return resumeData;
  } catch (err) {
    uploadWarn('Failed to load upload progress:', err);
    return null;
  }
};

/**
 * Clear upload progress from localStorage.
 */
const clearUploadProgress = (fileKey: string): void => {
  try {
    localStorage.removeItem(UPLOAD_RESUME_KEY_PREFIX + fileKey);
  } catch (err) {
    uploadWarn('Failed to clear upload progress:', err);
  }
};

/**
 * Reset OBS direct upload mode by clearing the failed flag.
 * Use this to retry direct upload after fixing network issues.
 */
export const resetObsDirectMode = (): void => {
  try {
    sessionStorage.removeItem(OBS_DIRECT_FAILED_KEY);
    uploadLog('[Upload:Config] OBS direct mode reset. Next upload will attempt direct connection.');
  } catch (err) {
    uploadWarn('[Upload:Config] Failed to reset OBS direct mode:', err);
  }
};

/**
 * Check if OBS direct mode is currently disabled.
 */
export const isObsDirectModeDisabled = (): boolean => {
  return sessionStorage.getItem(OBS_DIRECT_FAILED_KEY) === 'true';
};

/**
 * Get current upload configuration.
 * Useful for debugging and verification.
 */
export const getUploadConfig = () => {
  const concurrency = getMaxConcurrentUploads();
  const cachedProbe = getCachedProbeResult();

  return {
    multipartThreshold: MULTIPART_THRESHOLD / 1024 / 1024, // MB
    maxConcurrentUploads: concurrency,
    probeTimeout: OBS_PROBE_TIMEOUT / 1000, // seconds
    partUploadTimeout: PART_UPLOAD_TIMEOUT / 1000, // seconds
    isObsDirectDisabled: sessionStorage.getItem(OBS_DIRECT_FAILED_KEY) === 'true',
    defaultConcurrency: DEFAULT_MAX_CONCURRENT_UPLOADS,
    maxAllowedConcurrency: MAX_MAX_CONCURRENT_UPLOADS,
    // Cache info
    probeCacheTTL: OBS_PROBE_CACHE_TTL / 1000 / 60, // minutes
    cachedProbeResult: cachedProbe ? {
      reachable: cachedProbe.reachable,
      age: Math.round((Date.now() - cachedProbe.timestamp) / 1000), // seconds
    } : null,
  };
};

/**
 * Clear all expired upload progress entries.
 */
export const cleanupExpiredUploads = (): void => {
  try {
    const MAX_RESUME_AGE = 23 * 60 * 60 * 1000; // 23 hours
    const now = Date.now();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(UPLOAD_RESUME_KEY_PREFIX)) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const resumeData: UploadResumeData = JSON.parse(data);
            if (now - resumeData.timestamp > MAX_RESUME_AGE) {
              localStorage.removeItem(key);
            }
          }
        } catch (err) {
          // Invalid data, remove it
          localStorage.removeItem(key);
        }
      }
    }
  } catch (err) {
    uploadWarn('Failed to cleanup expired uploads:', err);
  }
};

/**
 * Clear ALL OBS-related caches and reset to clean state.
 * Use this to resolve persistent upload issues or after code updates.
 *
 * This will:
 * - Clear all upload resume data (including in-progress uploads)
 * - Clear OBS probe cache
 * - Reset direct mode flags
 * - Force re-probe and fresh upload sessions on next upload
 */
export const clearAllObsCache = (): void => {
  try {
    let clearedCount = 0;

    // 1. Clear all upload resume data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(UPLOAD_RESUME_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      clearedCount++;
    });

    // 2. Clear probe cache
    if (localStorage.getItem(OBS_PROBE_CACHE_KEY)) {
      localStorage.removeItem(OBS_PROBE_CACHE_KEY);
      clearedCount++;
    }

    // 3. Reset direct mode flag
    if (sessionStorage.getItem(OBS_DIRECT_FAILED_KEY)) {
      sessionStorage.removeItem(OBS_DIRECT_FAILED_KEY);
      clearedCount++;
    }

    uploadLog(`[OBS:Cache] Cleared ${clearedCount} cache entries`);
    uploadLog('[OBS:Cache] ✓ All upload resume data cleared');
    uploadLog('[OBS:Cache] ✓ Probe cache cleared');
    uploadLog('[OBS:Cache] ✓ Direct mode flags reset');
    uploadLog('[OBS:Cache] Next upload will start fresh with new probe and session');

    return;
  } catch (err) {
    uploadError('[OBS:Cache] Failed to clear cache:', err);
    throw err;
  }
};

/**
 * Interface for cached probe result
 */
interface ProbeResultCache {
  reachable: boolean;
  timestamp: number;
  probeUrl: string; // Store the URL that was probed
  failureReason?: string; // Reason for probe failure (CORS, timeout, network error, etc.)
}

/**
 * Get cached probe result from localStorage
 * Returns null if cache is missing or expired
 */
const getCachedProbeResult = (): ProbeResultCache | null => {
  try {
    const cached = localStorage.getItem(OBS_PROBE_CACHE_KEY);
    if (!cached) return null;

    const result: ProbeResultCache = JSON.parse(cached);

    // Check if cache is expired（失败结果保留更久，减少不可达时的重复探测）
    const ttl = result.reachable ? OBS_PROBE_CACHE_TTL : OBS_PROBE_FAILURE_CACHE_TTL;
    if (Date.now() - result.timestamp > ttl) {
      uploadLog('[Upload:Probe:Cache] Probe cache expired, will re-probe');
      localStorage.removeItem(OBS_PROBE_CACHE_KEY);
      return null;
    }

    const age = Math.round((Date.now() - result.timestamp) / 1000);

    // Extract hostname for cleaner logging
    let obsHost: string;
    try {
      obsHost = result.probeUrl ? new URL(result.probeUrl).hostname : 'unknown';
    } catch {
      obsHost = 'unknown';
    }

    if (result.reachable) {
      uploadLog(`[Upload:Probe:Cache] ✓ Using cached probe result: ${obsHost} reachable (age: ${age}s)`);
    } else {
      uploadWarn(`[Upload:Probe:Cache] ✗ Using cached probe result: ${obsHost} NOT reachable (age: ${age}s)`);
      if (result.failureReason) {
        uploadWarn(`[Upload:Probe:Cache] Cached failure reason: ${result.failureReason}`);
      }
    }

    return result;
  } catch (err) {
    uploadWarn('[Upload:Probe:Cache] Failed to read probe cache:', err);
    return null;
  }
};

/**
 * Save probe result to cache
 */
const setCachedProbeResult = (reachable: boolean, probeUrl: string, failureReason?: string): void => {
  try {
    const cache: ProbeResultCache = {
      reachable,
      timestamp: Date.now(),
      probeUrl,
      failureReason,
    };
    localStorage.setItem(OBS_PROBE_CACHE_KEY, JSON.stringify(cache));

    if (reachable) {
      uploadLog(`[Upload:Probe:Cache] Cached probe result: reachable=true`);
    } else {
      uploadLog(`[Upload:Probe:Cache] Cached probe result: reachable=false, reason="${failureReason}"`);
    }
  } catch (err) {
    uploadWarn('[Upload:Probe:Cache] Failed to save probe cache:', err);
  }
};

/**
 * Background probe OBS connectivity and cache the result.
 * This should be called on page load or when user enters upload page.
 * Returns cached result immediately if available, otherwise performs probe.
 */
export const backgroundProbeObs = async (): Promise<boolean> => {
  // Check cache first
  const cached = getCachedProbeResult();
  if (cached !== null) {
    // Update sessionStorage based on cached result
    if (!cached.reachable) {
      sessionStorage.setItem(OBS_DIRECT_FAILED_KEY, 'true');
    } else {
      sessionStorage.removeItem(OBS_DIRECT_FAILED_KEY);
    }
    return cached.reachable;
  }

  uploadLog('[Upload:Probe:Background] No valid cache, performing background probe...');

  try {
    // Get a test signed URL from backend
    const { url } = await getUploadUrl(
      '__probe_test__.txt',
      'text/plain'
    );

    // 首屏只探一次：重试仅为规避偶发抖动，而上传本身已有 3 次直传重试兜底，
    // 这里多轮重试（3 次 × 超时 + 2 次 2s 退避）只会拖长首屏，收益不成正比。
    const result = await probeObsConnectivityWithRetry(url, 0);

    // Cache the result (including failure reason)
    setCachedProbeResult(result.reachable, url, result.reason);

    // Update sessionStorage
    if (!result.reachable) {
      sessionStorage.setItem(OBS_DIRECT_FAILED_KEY, 'true');
      uploadWarn('[Upload:Probe:Background] ✗ OBS not reachable, will use proxy mode');
      uploadWarn(`[Upload:Probe:Background] Reason: ${result.reason}`);
    } else {
      sessionStorage.removeItem(OBS_DIRECT_FAILED_KEY);
      uploadLog('[Upload:Probe:Background] ✓ OBS reachable, direct mode available');
    }

    return result.reachable;
  } catch (err) {
    uploadWarn('[Upload:Probe:Background] Background probe failed with exception:', err);
    const reason = `Backend error: ${err}`;
    // On error, assume unreachable and cache for shorter time
    setCachedProbeResult(false, '', reason);
    sessionStorage.setItem(OBS_DIRECT_FAILED_KEY, 'true');
    return false;
  }
};

/**
 * Clear probe cache. Use this to force re-probe on next upload.
 */
export const clearProbeCache = (): void => {
  try {
    localStorage.removeItem(OBS_PROBE_CACHE_KEY);
    sessionStorage.removeItem(OBS_DIRECT_FAILED_KEY);
    uploadLog('[Upload:Probe:Cache] Probe cache cleared');
  } catch (err) {
    uploadWarn('[Upload:Probe:Cache] Failed to clear probe cache:', err);
  }
};

/**
 * Upload multiple parts concurrently with a limit on concurrent uploads.
 */
const FATAL_PART_ERRORS = ['DIRECT_MODE_STALLED', 'Upload aborted by user', 'Upload cancelled by user'];

// 传输层"链路已经不可用"的错误。必须覆盖 axios 自己的超时文本，否则断流后
// 熔断器永远不触发，调度池会一直派发注定失败的分片。
const isTransportError = (message: string): boolean => {
  const msg = message.toLowerCase();
  return msg.includes('upload_stalled') ||
         msg.includes('econnaborted') ||
         msg.includes('etimedout') ||
         msg.includes('timeout of') ||
         msg.includes('network error') ||
         msg.includes('network or cors error') ||
         msg.includes('failed to fetch') ||
         msg.includes('err_network');
};

const isStallError = (message: string): boolean =>
  message.toLowerCase().includes('upload_stalled') ||
  message.toLowerCase().includes('no upload progress');

const uploadPartsConcurrently = async <T>(
  tasks: Array<() => Promise<T>>,
  maxConcurrent: number
): Promise<T[]> => {
  const results: (T | null)[] = new Array(tasks.length).fill(null);
  const errors: Map<number, Error> = new Map();
  const executing: Promise<void>[] = [];
  let fatalError: Error | null = null;
  let consecutiveNetworkErrors = 0;
  const NETWORK_FATAL_THRESHOLD = 3;

  for (const [index, task] of tasks.entries()) {
    // 检测到致命错误（需要切换模式），立即停止派发新分片
    if (fatalError) break;

    const promise = task()
      .then((result) => {
        results[index] = result;
        consecutiveNetworkErrors = 0;
        uploadLog(`[Upload:Concurrent:${index + 1}/${tasks.length}] ✓ Success`);
      })
      .catch((error) => {
        errors.set(index, error);
        uploadError(`[Upload:Concurrent:${index + 1}/${tasks.length}] ✗ Failed: ${error.message}`);
        if (FATAL_PART_ERRORS.some(tag => error.message.includes(tag))) {
          fatalError = error;
        } else if (isTransportError(error.message)) {
          consecutiveNetworkErrors++;
          if (consecutiveNetworkErrors >= NETWORK_FATAL_THRESHOLD) {
            uploadError(`[Upload:Concurrent] ${NETWORK_FATAL_THRESHOLD} consecutive transport errors, stopping dispatch for current file`);
            fatalError = error;
          }
        } else {
          consecutiveNetworkErrors = 0;
        }
      })
      .finally(() => {
        executing.splice(executing.indexOf(promise), 1);
      });

    executing.push(promise);

    if (executing.length >= maxConcurrent) {
      await Promise.race(executing);
    }
  }

  await Promise.all(executing);

  // 致命错误直接抛出原始错误，保留错误标识供上层识别
  if (fatalError) {
    throw fatalError;
  }

  // Check for errors and generate detailed error message
  if (errors.size > 0) {
    const totalParts = tasks.length;
    const failedCount = errors.size;
    const failedIndices = Array.from(errors.keys()).map(i => i + 1);

    // Categorize error types
    const errorTypes: Record<string, number> = {};
    errors.forEach(error => {
      const msg = error.message.toLowerCase();
      if (msg.includes('upload_stalled') || msg.includes('no upload progress')) errorTypes.stalled = (errorTypes.stalled || 0) + 1;
      else if (msg.includes('timeout')) errorTypes.timeout = (errorTypes.timeout || 0) + 1;
      else if (msg.includes('503')) errorTypes.service_unavailable = (errorTypes.service_unavailable || 0) + 1;
      else if (msg.includes('network') || msg.includes('failed to fetch')) errorTypes.network = (errorTypes.network || 0) + 1;
      else if (msg.includes('url_expired')) errorTypes.url_expired = (errorTypes.url_expired || 0) + 1;
      else errorTypes.other = (errorTypes.other || 0) + 1;
    });

    // Generate user-friendly error type descriptions
    const errorTypeDesc = [];
    if (errorTypes.stalled) errorTypeDesc.push(`${errorTypes.stalled} 个分片上传停滞无进度（可能是带宽被多文件并发上传抢占）`);
    if (errorTypes.timeout) errorTypeDesc.push(`${errorTypes.timeout} 个分片超时（可能是网络不稳定）`);
    if (errorTypes.service_unavailable) errorTypeDesc.push(`${errorTypes.service_unavailable} 个分片遇到服务暂时不可用（503）`);
    if (errorTypes.network) errorTypeDesc.push(`${errorTypes.network} 个分片遇到网络错误`);
    if (errorTypes.url_expired) errorTypeDesc.push(`${errorTypes.url_expired} 个分片的上传链接过期`);
    if (errorTypes.other) errorTypeDesc.push(`${errorTypes.other} 个分片遇到其他错误`);

    // Generate actionable suggestions
    const suggestions = [];
    if (errorTypes.timeout || errorTypes.network) {
      suggestions.push('• 检查网络连接是否稳定');
    }
    if (errorTypes.stalled) {
      suggestions.push('• 避免同时上传多个超大文件，可分开逐个上传');
    }
    if (errorTypes.service_unavailable) {
      suggestions.push('• 等待几分钟后重试');
    }
    suggestions.push('• 稍后重试（已保存上传进度）');
    if (failedCount > 1) {
      suggestions.push('• 如问题持续，请联系管理员');
    }

    // Limit detailed error list length
    const maxDetailedErrors = 5;
    const detailedErrors = Array.from(errors.entries())
      .slice(0, maxDetailedErrors)
      .map(([idx, err]) => `分片 ${idx + 1}: ${err.message}`)
      .join('\n');
    const hasMoreErrors = errors.size > maxDetailedErrors;

    // Build complete error message
    const errorMessage = [
      `上传失败：${failedCount}/${totalParts} 个分片上传失败`,
      '',
      ...errorTypeDesc.map(desc => `• ${desc}`),
      '',
      `失败的分片：${failedIndices.join(', ')}`,
      '',
      '建议：',
      ...suggestions,
      '',
      '详细错误：',
      detailedErrors,
      hasMoreErrors ? `... 及其他 ${errors.size - maxDetailedErrors} 个错误` : '',
    ].filter(line => line !== undefined).join('\n');

    uploadError('[Upload:Concurrent] Upload failed summary:', {
      totalParts,
      failedCount,
      failedParts: failedIndices,
      errorTypes,
    });

    throw new Error(errorMessage);
  }

  return results.filter((r): r is T => r !== null);
};

/**
 * Interface for probe result with detailed failure information
 */
interface ProbeResult {
  reachable: boolean;
  reason?: string; // Detailed failure reason (CORS, timeout, network error, etc.)
  url: string;     // The URL that was probed
}

/**
 * Probe OBS connectivity with a lightweight HEAD request.
 * Returns detailed result including failure reason for diagnostics.
 * Note: Pre-signed URLs are method-bound (PUT), so HEAD returns 403 — that
 * still proves OBS is reachable, hence treated as success.
 */
const probeObsConnectivity = (url: string): Promise<ProbeResult> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const xhr = new XMLHttpRequest();

    // Extract hostname for cleaner logging
    let obsHost: string;
    try {
      obsHost = new URL(url).hostname;
    } catch {
      obsHost = 'unknown';
    }

    xhr.open('HEAD', url);
    xhr.timeout = OBS_PROBE_TIMEOUT;

    xhr.onload = () => {
      const duration = Date.now() - startTime;
      uploadLog(`[Upload:Probe] ✓ OBS reachable: ${obsHost} (status: ${xhr.status}, duration: ${duration}ms)`);
      resolve({ reachable: true, url });  // Any HTTP response means OBS is reachable
    };

    xhr.onerror = () => {
      const duration = Date.now() - startTime;
      const status = xhr.status;
      const statusText = xhr.statusText || 'unknown';

      // xhr.status === 0 typically indicates CORS error or network failure
      let reason: string;
      if (status === 0) {
        reason = `CORS or network error (blocked by browser)`;
      } else {
        reason = `HTTP error ${status} ${statusText}`;
      }

      uploadWarn(`[Upload:Probe] ✗ OBS probe failed: ${obsHost} - ${reason} (duration: ${duration}ms)`);
      uploadWarn(`[Upload:Probe] Target URL: ${url}`);
      resolve({ reachable: false, reason, url });
    };

    xhr.ontimeout = () => {
      const reason = `Timeout after ${OBS_PROBE_TIMEOUT}ms`;
      uploadWarn(`[Upload:Probe] ✗ OBS probe failed: ${obsHost} - ${reason}`);
      uploadWarn(`[Upload:Probe] Target URL: ${url}`);
      xhr.abort();
      resolve({ reachable: false, reason, url });
    };

    xhr.send();
  });
};

/**
 * Probe OBS connectivity with retry mechanism.
 * Retries on failure to reduce false negatives from temporary network hiccups.
 *
 * @param url - The URL to probe
 * @param retries - Number of retry attempts (default: 2, total 3 attempts)
 * @returns Promise<ProbeResult> - detailed probe result including failure reason
 */
const probeObsConnectivityWithRetry = async (url: string, retries = 2): Promise<ProbeResult> => {
  let lastResult: ProbeResult | null = null;

  for (let i = 0; i <= retries; i++) {
    const attemptNum = i + 1;
    const totalAttempts = retries + 1;

    uploadLog(`[Upload:Probe:Retry] Attempt ${attemptNum}/${totalAttempts}...`);

    try {
      const result = await probeObsConnectivity(url);
      lastResult = result;

      if (result.reachable) {
        if (i > 0) {
          uploadLog(`[Upload:Probe:Retry] ✓ Success on attempt ${attemptNum}/${totalAttempts}`);
        }
        return result;
      }

      // Failed, but not the last attempt - wait before retry
      if (i < retries) {
        const waitTime = 2000; // 2 seconds between retries
        uploadLog(`[Upload:Probe:Retry] ✗ Attempt ${attemptNum} failed: ${result.reason}`);
        uploadLog(`[Upload:Probe:Retry] Waiting ${waitTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    } catch (err) {
      uploadError(`[Upload:Probe:Retry] Attempt ${attemptNum} error:`, err);
      lastResult = { reachable: false, reason: `Exception: ${err}`, url };

      // If it's not the last attempt, wait before retry
      if (i < retries) {
        const waitTime = 2000;
        uploadLog(`[Upload:Probe:Retry] Waiting ${waitTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }

  uploadWarn(`[Upload:Probe:Retry] ✗ All ${retries + 1} attempts failed`);
  if (lastResult) {
    uploadWarn(`[Upload:Probe:Retry] Final failure reason: ${lastResult.reason}`);
  }
  return lastResult || { reachable: false, reason: 'All retries failed', url };
};

/**
 * Upload a file to OBS.
 * Always tries direct OBS upload first (up to 3 attempts),
 * then falls back to backend proxy (up to 3 attempts) if all direct attempts fail.
 * Chunk size is determined automatically by file size.
 */
export const uploadFile = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  _config?: UploadConfig,
  onAutoRetry?: () => void,
  uploadKey?: string,
  onObjectKeyReady?: (objectKey: string) => void,
): Promise<UploadResult> => {
  uploadLog('[Upload] Starting file upload:', {
    name: file.name,
    size: file.size,
    type: file.type || 'application/octet-stream',
    lastModified: new Date(file.lastModified).toISOString(),
  });

  const chunkSize = getChunkSizeByFileSize(file.size);
  const isLargeFile = file.size > chunkSize;
  uploadLog(`[Upload] chunk size: ${(chunkSize / 1024 / 1024).toFixed(0)}MB, multipart: ${isLargeFile}`);

  const userUploadMode = useAuthStore.getState().user?.upload_mode ?? 'auto';

  // --- Direct mode: up to 3 attempts (skipped when user upload_mode is 'proxy') ---
  let lastDirectError: unknown;
  if (userUploadMode !== 'proxy') for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      if (attempt > 1) onAutoRetry?.();
      uploadLog(`[Upload] Direct attempt ${attempt}/3`);
      if (isLargeFile) {
        return await uploadMultipart(file, onProgress, chunkSize, 'Direct', onObjectKeyReady);
      }
      const { url, object_key } = await getUploadUrl(file.name, file.type || 'application/octet-stream');
      onObjectKeyReady?.(object_key);
      return await uploadSingleWithUrl(file, url, object_key, onProgress, 'Direct', uploadKey);
    } catch (err) {
      lastDirectError = err;
      const msg = err instanceof Error ? err.message : String(err);
      uploadWarn(`[Upload] Direct attempt ${attempt} failed:`, err);
      if (FATAL_PART_ERRORS.some(tag => msg.includes(tag))) {
        uploadWarn('[Upload] 直传模式不可用，跳过剩余重试，切换代理模式');
        break;
      }
    }
  }

  if (userUploadMode === 'proxy') {
    const username = useAuthStore.getState().user?.username ?? 'unknown';
    uploadLog(`[Upload] 用户${username}配置为代理模式上传`);
  } else {
    uploadWarn('[Upload] All direct attempts failed, switching to PROXY mode');
  }
  if (onProgress) {
    onProgress({ loaded: 0, total: file.size, percent: 0, status: 'uploading' });
  }

  // --- Proxy mode: up to 3 attempts ---
  let lastProxyError: unknown;
  for (let attempt = 1; attempt <= 3; attempt++) {
    // 用户已主动取消，立即停止，不再发起新的代理请求
    if (uploadKey && cancelledUploadKeys.has(uploadKey)) {
      cancelledUploadKeys.delete(uploadKey);
      uploadLog(`[Upload] Proxy attempt ${attempt} skipped: upload cancelled by user`);
      throw new Error('Upload cancelled by user');
    }
    try {
      onAutoRetry?.();
      uploadLog(`[Upload] Proxy attempt ${attempt}/3`);
      return await uploadViaProxy(file, onProgress, { chunkSize } as UploadConfig, uploadKey);
    } catch (err) {
      lastProxyError = err;
      const errMsg = err instanceof Error ? err.message : String(err);
      uploadWarn(`[Upload] Proxy attempt ${attempt} failed:`, err);
      // 本次 attempt 因 abort/cancel 失败，不再重试
      if (errMsg.includes('aborted') || errMsg.includes('cancelled') || errMsg.includes('cancel')) {
        break;
      }
    }
  }

  const finalError = lastProxyError ?? lastDirectError;
  uploadError('[Upload] All proxy attempts failed');
  reportUploadEvent('upload_error', {
    level: 'error',
    file_name: file.name,
    detail: {
      mode: userUploadMode === 'proxy' ? 'Proxy' : 'Proxy(after direct failed)',
      attempts: 3,
      file_size: file.size,
      error: finalError instanceof Error ? finalError.message : String(finalError),
    },
  }).catch(() => {});
  throw finalError;
};

/**
 * Single file upload (for files < 100MB).
 * Accepts pre-fetched signed URL and object key from uploadFile.
 * ✅ Supports upload cancellation via uploadId
 */
const uploadSingleWithUrl = async (
  file: File,
  url: string,
  object_key: string,
  onProgress?: (progress: UploadProgress) => void,
  mode: string = 'Direct',  // Upload mode for logging
  uploadId?: string  // ✅ NEW: Unique ID for cancellation support
): Promise<UploadResult> => {
  const actualUploadId = uploadId || generateUploadId(file);
  const startTime = new Date().toISOString();

  // ✅ Register upload task
  activeUploads.set(actualUploadId, { file, aborted: false });

  try {
    // Step 1: Calculate MD5
    uploadLog('[Upload:MD5] Calculating file MD5...');
    let md5Hex = '';
    try {
      md5Hex = await calculateFileMD5(file);
      uploadLog('[Upload:MD5] MD5 calculated:', md5Hex);
    } catch (err) {
      uploadWarn('[Upload:MD5] Failed to calculate MD5:', err);
    }

    const uploadOnce = () => new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // ✅ Save xhr reference for potential cancellation
      const task = activeUploads.get(actualUploadId);
      if (task) {
        task.xhr = xhr;
        // Check if already aborted before starting
        if (task.aborted) {
          reject(new Error('Upload aborted'));
          return;
        }
      }

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress({
            loaded: e.loaded,
            total: e.total,
            percent: Math.round((e.loaded / e.total) * 100),
          });
        }
      };

      xhr.onload = () => {
        // ✅ Remove from active uploads on completion
        activeUploads.delete(actualUploadId);

        if (xhr.status >= 200 && xhr.status < 300) {
          // ✅ 立即记录收到的ETag
          const etag = xhr.getResponseHeader('ETag') || '';
          uploadLog(`[Upload:Single:Received] Got ETag from OBS: ${etag.substring(0, 32)}...`);

          // ✅ 然后进行ETag校验
          const etagMD5 = etag.replace(/"/g, '').toLowerCase();
          const expectedMD5 = md5Hex.toLowerCase();

          if (md5Hex && etagMD5 !== expectedMD5) {
            uploadError(`[Upload:Single:ETag] MD5 mismatch! Expected: ${expectedMD5}, Got: ${etagMD5}`);

            // ✅ 通知后端ETag校验失败（异步处理，不等待）
            import('../api/storage').then(({ notifyETagMismatch }) => {
              notifyETagMismatch({
                file_name: file.name,
                file_size: file.size,
                mode: 'Direct',
                expected_md5: expectedMD5,
                received_etag: etagMD5,
                timestamp: new Date().toISOString(),
                error_message: 'ETag verification failed for single file upload'
              }).catch(() => {}); // Ignore notification errors
            });

            reject(new Error(`File MD5 mismatch after upload`));
            return;
          }

          if (md5Hex) {
            uploadLog(`[Upload:Single:ETag] ✓ MD5 verified: ${expectedMD5}`);
          }
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.timeout = PART_UPLOAD_TIMEOUT;
      xhr.onerror = () => {
        activeUploads.delete(actualUploadId);
        reject(new Error(`Upload failed (network error, status: ${xhr.status})`));
      };
      xhr.ontimeout = () => {
        activeUploads.delete(actualUploadId);
        xhr.abort();
        reject(new Error('Upload timeout'));
      };
      xhr.send(file);
    });

    // Retry logic for single file upload with exponential backoff for 503
    const targetUrl = url.split('?')[0]; // Target URL without query params (for logging)
    uploadLog(`[Upload:Single] Starting single file upload, file size: ${file.size}, object_key: ${object_key}, target: ${targetUrl}`);
    let lastError: Error | undefined;
  for (let attempt = 0; attempt < MAX_PART_RETRIES; attempt++) {
    try {
      uploadLog(`[Upload:Single] Attempt ${attempt + 1}/${MAX_PART_RETRIES}`);
      await uploadOnce();
      uploadLog('[Upload:Single] Upload successful');

      // ✅ Fix 1P: Verify file exists and is accessible in OBS
      uploadLog(`[Upload:Single:Verify] Starting file verification...`);
      try {
        const verifyResult = await verifyFile(object_key, file.size);
        if (!verifyResult.exists) {
          throw new Error(`Upload verification failed: File does not exist in OBS.`);
        }
        // ✅ Protect against undefined size
        if (verifyResult.size !== undefined && verifyResult.size !== file.size) {
          throw new Error(
            `File size mismatch: expected ${file.size} bytes, got ${verifyResult.size} bytes`
          );
        }
        uploadLog(`[Upload:Single:Verify] ✅ File verified! Size: ${verifyResult.size} bytes`);
      } catch (verifyErr) {
        uploadError(`[Upload:Single:Verify] ❌ Verification failed:`, verifyErr);
        throw verifyErr;
      }

      // Notify backend for upload logging (fire-and-forget)
      const endTime = new Date().toISOString();
      const duration = ((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000).toFixed(2);
      const uploadSummary = {
        file_name: file.name,
        file_size: file.size,
        start_time: startTime,
        end_time: endTime,
        duration: `${duration}s`,
        mode: mode,
        chunk_size: 'N/A',  // Single file upload, no chunks
        result: 'success' as const,
      };
      uploadLog(`[upload-summary] ${JSON.stringify(uploadSummary)}`);
      notifyUploadComplete(uploadSummary).catch(err => uploadWarn('[Upload] Failed to notify backend:', err));

      break; // Success, exit retry loop
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      uploadWarn(`[Upload:Single] Attempt ${attempt + 1}/${MAX_PART_RETRIES} failed: ${lastError.message}`);

      if (attempt < MAX_PART_RETRIES - 1) {
        const is503 = lastError.message.includes('503');
        const delay = is503
          ? Math.min(1000 * Math.pow(2, attempt), 10000)
          : 1000 * (attempt + 1);

        if (is503) {
          uploadWarn(`[Upload:Single] OBS service unavailable (503), retrying after ${delay}ms...`);
        } else {
          uploadWarn(`[Upload:Single] Retrying after ${delay}ms...`);
        }

        await new Promise((r) => setTimeout(r, delay));
      } else {
        throw lastError;
      }
    }
  }

  // Construct file URL (remove query params from signed URL)
  const fileUrl = url.split('?')[0];

  return {
    objectKey: object_key,
    fileUrl,
    fileName: file.name,
    fileSize: file.size,
    md5: md5Hex,  // Return MD5 in hex format for storage and display
  };
} finally {
  // ✅ Cleanup: Remove from active uploads if not already removed
  activeUploads.delete(actualUploadId);
}
};

/**
 * Multipart upload (for files > 100MB) with resume support and ETag verification.
 *
 * Supports dynamic chunk sizing based on network speed.
 *
 * Integrity protection:
 * - Computes MD5 for each part and verifies against OBS-returned ETag
 * - Computes overall file MD5 in parallel for post-upload verification
 * - Automatically retries on ETag mismatch
 *
 * Note: Content-MD5 header is not supported for presigned URL multipart uploads
 * due to signature limitations. ETag verification provides equivalent protection.
 */
const uploadMultipart = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  chunkSize?: number,
  mode: string = 'Direct',
  onObjectKeyReady?: (objectKey: string) => void,
): Promise<UploadResult> => {
  const startTime = new Date().toISOString();
  const fileKey = getFileKey(file);

  // Start MD5 calculation in parallel (don't wait)
  const md5Promise = calculateFileMD5(file).catch((err) => {
    uploadWarn('[Upload:Multipart:MD5] Failed to calculate MD5:', err);
    return '';
  });
  uploadLog('[Upload:Multipart:MD5] Started MD5 calculation in background');

  // Determine effective chunk size
  const resumeData = loadUploadProgress(fileKey);
  let effectiveChunkSize: number;

  if (resumeData && resumeData.fileName === file.name && resumeData.fileSize === file.size) {
    // Check if user changed chunk size - if so, restart upload
    if (chunkSize && resumeData.chunkSize && chunkSize !== resumeData.chunkSize) {
      uploadWarn(
        `[Upload:Resume] Chunk size changed from ${resumeData.chunkSize / 1024 / 1024}MB to ${chunkSize / 1024 / 1024}MB, ` +
        `clearing progress and restarting upload`
      );
      clearUploadProgress(fileKey);
      effectiveChunkSize = chunkSize;
    } else {
      // Resume: must use original chunk size to maintain part boundaries
      effectiveChunkSize = resumeData.chunkSize;
      uploadLog(`[Upload:Resume] Using saved chunk size: ${effectiveChunkSize / 1024 / 1024}MB`);
    }
  } else {
    effectiveChunkSize = chunkSize!;
    uploadLog(`[Upload:New] Using chunk size: ${effectiveChunkSize / 1024 / 1024}MB`);
  }

  const totalParts = Math.ceil(file.size / effectiveChunkSize);
  uploadLog(`[Upload:Multipart] Starting multipart upload for ${file.name}, size: ${file.size}, chunk: ${effectiveChunkSize / 1024 / 1024}MB, total parts: ${totalParts}`);

  // Check for existing upload progress (already loaded above for chunk size)
  let upload_id: string | undefined;
  let object_key: string | undefined;
  let part_urls: Array<{ part_number: number; url: string }> | undefined;
  let parts: Array<{ part_number: number; etag: string }> = [];
  let uploadedBytes = 0;

  // Try to resume from existing upload session
  if (resumeData && resumeData.fileName === file.name && resumeData.fileSize === file.size) {
    // Check if upload session is too old (OBS sessions may expire after 1-2 hours)
    const SESSION_MAX_AGE = 60 * 60 * 1000; // 1 hour
    const sessionAge = Date.now() - resumeData.timestamp;

    if (sessionAge > SESSION_MAX_AGE) {
      const ageMinutes = Math.round(sessionAge / 60000);
      uploadWarn(`[Upload:Multipart] Upload session too old (${ageMinutes} minutes), clearing and restarting...`);
      uploadWarn('[Upload:Multipart] Old upload sessions may be expired by OBS, starting fresh upload');
      clearUploadProgress(fileKey);
      // Don't set upload_id, will fall through to new upload initialization
    } else {
      // Resume existing upload
      uploadLog(`[Upload:Multipart] Resuming upload for ${file.name}, ${resumeData.completedParts.length}/${totalParts} parts already uploaded (session age: ${Math.round(sessionAge / 60000)} minutes)`);
      upload_id = resumeData.uploadId;
      object_key = resumeData.objectKey;
      parts = [...resumeData.completedParts];

      // Calculate already uploaded bytes
      uploadedBytes = resumeData.completedParts.reduce((sum, part) => {
        const start = (part.part_number - 1) * effectiveChunkSize;
        const end = Math.min(start + effectiveChunkSize, file.size);
        return sum + (end - start);
      }, 0);

      uploadLog(`[Upload:Multipart] Already uploaded ${uploadedBytes} bytes (${Math.round(uploadedBytes / file.size * 100)}%)`);

      // Refresh signed URLs for existing upload session (without creating new session)
      uploadLog('[Upload:Multipart] Refreshing signed URLs for existing session...');
      let refreshResult: Awaited<ReturnType<typeof refreshMultipartUrls>>;
      try {
        refreshResult = await refreshMultipartUrls(
          upload_id,
          object_key,
          file.size,
          effectiveChunkSize  // Pass chunk size to backend
        );
      } catch (refreshErr) {
        const refreshStatus = (refreshErr as any)?.response?.status;
        const refreshErrorCode = (refreshErr as any)?.response?.data?.error;
        if (refreshStatus === 409 && refreshErrorCode === 'upload_already_completed') {
          uploadWarn('[Upload:Multipart] upload_id already completed/aborted, clearing cache and restarting');
          clearUploadProgress(fileKey);
          throw new Error('UPLOAD_SESSION_EXPIRED: upload_id no longer valid, please retry');
        }
        throw refreshErr;
      }
      part_urls = refreshResult.part_urls;
      uploadLog(`[Upload:Multipart] Refreshed ${part_urls.length} signed URLs for existing upload_id=${upload_id.substring(0, 20)}...`);

      // Show initial progress
      if (onProgress) {
        const percent = Math.min(Math.round((uploadedBytes / file.size) * 95), 95);
        onProgress({
          loaded: uploadedBytes,
          total: file.size,
          percent,
        });
      }
    }
  }

  // Start new upload if no upload_id was set (either no resume data or session too old)
  if (!upload_id) {
    const expectedParts = Math.ceil(file.size / effectiveChunkSize);
    uploadLog(`[Upload:Multipart] Initializing new multipart upload (expected ${expectedParts} parts for ${file.size} bytes)...`);
    const result = await initMultipartUpload(
      file.name,
      file.type || 'application/octet-stream',
      file.size,
      effectiveChunkSize  // Pass chunk size to backend
    );
    upload_id = result.upload_id;
    object_key = result.object_key;
    part_urls = result.part_urls;
    onObjectKeyReady?.(object_key);
    uploadLog(`[Upload:Multipart] Initialized: upload_id=${upload_id.substring(0, 20)}..., object_key=${object_key}, parts=${part_urls.length}`);

    if (part_urls.length !== expectedParts) {
      uploadWarn(`[Upload:Multipart] Warning: Backend returned ${part_urls.length} part URLs, but expected ${expectedParts} parts!`);
    }
  }

  // Ensure all variables are initialized
  if (!upload_id || !object_key || !part_urls) {
    throw new Error('[Upload:Multipart] Failed to initialize upload: missing upload_id, object_key, or part_urls');
  }

  // Create mutable references for use in closures (TypeScript narrowing doesn't work well with captured variables)
  let part_urls_mutable = part_urls;

  // Upload each part with concurrency (占总进度的 0-95%)
  const completedPartNumbers = new Set(parts.map(p => p.part_number));
  const remainingParts = part_urls_mutable.filter(p => !completedPartNumbers.has(p.part_number));

  uploadLog(`[Upload:Multipart] Part URLs received: ${part_urls_mutable.length}`);
  uploadLog(`[Upload:Multipart] Already completed: ${parts.length} parts [${Array.from(completedPartNumbers).sort((a,b) => a-b).join(', ')}]`);
  uploadLog(`[Upload:Multipart] Starting to upload ${remainingParts.length} remaining parts (${parts.length} already completed)`);

  // Track progress for each part being uploaded
  const partProgress = new Map<number, number>();
  let lastReportedPercent = Math.min(Math.round((uploadedBytes / file.size) * 95), 95);
  const partsMutex = new AsyncMutex();
  let unsavedPartsCount = 0; // Batch counter for localStorage writes

  // Speed tracking
  const uploadStartTime = Date.now();
  const startBytes = uploadedBytes;
  let lastSpeedLogTime = uploadStartTime;
  let lastSpeedLogBytes = startBytes;
  let lastCurrentSpeed = 0; // Track most recent current speed

  const updateProgress = () => {
    if (!onProgress) return;

    const currentProgress = uploadedBytes + Array.from(partProgress.values()).reduce((sum, p) => sum + p, 0);
    const percent = Math.min(Math.round((currentProgress / file.size) * 95), 95);

    // Calculate speed (bytes per second)
    const now = Date.now();
    const elapsedSeconds = (now - uploadStartTime) / 1000;
    const uploadedSinceStart = currentProgress - startBytes;
    const speed = elapsedSeconds > 0 ? uploadedSinceStart / elapsedSeconds : 0;
    const remainingBytes = file.size - currentProgress;
    const remainingTime = speed > 0 ? remainingBytes / speed : 0;

    // Calculate current speed (real-time, based on recent data)
    const recentBytes = currentProgress - lastSpeedLogBytes;
    const recentSeconds = (now - lastSpeedLogTime) / 1000;
    const currentSpeed = recentSeconds > 0 ? recentBytes / recentSeconds : 0;

    // Log speed every 5 seconds
    if (now - lastSpeedLogTime >= 5000) {
      lastCurrentSpeed = currentSpeed; // Update tracked current speed
      const speedMBps = (currentSpeed / 1024 / 1024).toFixed(2);
      const avgSpeedMBps = (speed / 1024 / 1024).toFixed(2);
      const eta = remainingTime > 60 ? `${Math.round(remainingTime / 60)}min` : `${Math.round(remainingTime)}s`;
      uploadLog(`[Upload:Speed] ${percent}% | Current: ${speedMBps} MB/s | Avg: ${avgSpeedMBps} MB/s | ETA: ${eta}`);
      lastSpeedLogTime = now;
      lastSpeedLogBytes = currentProgress;
    }

    // Only update if percent changed to reduce UI updates
    if (percent > lastReportedPercent) {
      lastReportedPercent = percent;
      onProgress({
        loaded: currentProgress,
        total: file.size,
        percent,
        speed,
        currentSpeed: lastCurrentSpeed > 0 ? lastCurrentSpeed : speed, // Use last current speed or fallback to average
        remainingTime,
      });
    }
  };

  // Global URL refresh lock to prevent concurrent refresh calls
  let urlRefreshPromise: Promise<void> | null = null;
  let urlRefreshCount = 0;
  const MAX_URL_REFRESH = 3; // Maximum number of URL refreshes

  /**
   * Refresh all part URLs (globally, only once at a time)
   */
  const refreshAllPartUrls = async (): Promise<void> => {
    // If already refreshing, wait for that to complete
    if (urlRefreshPromise) {
      uploadLog('[Upload:Multipart:URLRefresh] Already refreshing, waiting...');
      return urlRefreshPromise;
    }

    // Check refresh limit
    if (urlRefreshCount >= MAX_URL_REFRESH) {
      throw new Error(`URL refresh limit reached (${MAX_URL_REFRESH} attempts)`);
    }

    urlRefreshCount++;
    uploadLog(`[Upload:Multipart:URLRefresh] Starting global URL refresh (attempt ${urlRefreshCount}/${MAX_URL_REFRESH})...`);

    urlRefreshPromise = (async () => {
      try {
        const refreshResult = await refreshMultipartUrls(
          upload_id,
          object_key,
          file.size,
          effectiveChunkSize  // Pass chunk size to backend
        );

        // Update global part_urls array with new URLs
        part_urls_mutable = refreshResult.part_urls;
        uploadLog(`[Upload:Multipart:URLRefresh] Successfully refreshed ${part_urls_mutable.length} part URLs`);
      } finally {
        // Clear the promise so next refresh can proceed
        urlRefreshPromise = null;
      }
    })();

    return urlRefreshPromise;
  };

  // 连续失败分片计数：连续 3 个分片最终失败则中止直传，快速切换代理模式
  let consecutivePartFailures = 0;
  const CONSECUTIVE_FAIL_THRESHOLD = 3;

  // Create upload tasks for remaining parts
  const uploadTasks = remainingParts.map(partInfo => async () => {
    const start = (partInfo.part_number - 1) * effectiveChunkSize;
    const end = Math.min(start + effectiveChunkSize, file.size);
    const chunk = file.slice(start, end);

    uploadLog(`[Upload:Multipart] Uploading part ${partInfo.part_number}/${totalParts}, size: ${chunk.size}`);

    // ✅ 保留：计算分片MD5用于ETag校验
    let chunkMD5Hex = '';
    try {
      const chunkArrayBuffer = await chunk.arrayBuffer();
      const chunkSpark = new SparkMD5.ArrayBuffer();
      chunkSpark.append(chunkArrayBuffer);
      chunkMD5Hex = chunkSpark.end();
      uploadLog(`[Upload:Part:MD5] Part ${partInfo.part_number} MD5: ${chunkMD5Hex}`);
    } catch (err) {
      uploadWarn(`[Upload:Part:MD5] Failed to calculate MD5 for part ${partInfo.part_number}:`, err);
      // MD5计算失败不阻塞上传，继续不带MD5校验上传
    }

    // 每个分片最多触发一次 URL 刷新：刷新后再失败直接算该分片失败
    let urlRefreshedForThisPart = false;
    let lastUrlError: Error | undefined;

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        // Get current URL for this part from global array
        const currentPartInfo = part_urls_mutable.find(p => p.part_number === partInfo.part_number);
        if (!currentPartInfo) {
          throw new Error(`Part ${partInfo.part_number} not found in part_urls`);
        }

        const etag = await uploadPart(
          currentPartInfo.url,
          chunk,
          (loaded) => {
            partProgress.set(partInfo.part_number, loaded);
            updateProgress();
          },
          chunkMD5Hex,  // expectedMD5Hex
          partInfo.part_number,  // partNumber
          file.name,  // fileName
          file.size   // fileSize
        );

        // Success! Save part
        consecutivePartFailures = 0;
        const newPart = { part_number: partInfo.part_number, etag };

        // Thread-safe parts array update
        await partsMutex.lock();
        try {
          parts.push(newPart);
          unsavedPartsCount++;

          // Batch save: save every N parts or on last part
          const isLastPart = parts.length === totalParts;
          const shouldSave = unsavedPartsCount >= SAVE_PROGRESS_BATCH_SIZE || isLastPart;

          if (shouldSave) {
            saveUploadProgress(fileKey, upload_id, object_key, file.name, file.size, parts, effectiveChunkSize);
            unsavedPartsCount = 0;
            uploadLog(`[Upload:Multipart] Part ${partInfo.part_number}/${totalParts} uploaded (progress saved, batch_size=${SAVE_PROGRESS_BATCH_SIZE}), etag: ${etag.substring(0, 20)}...`);
          } else {
            uploadLog(`[Upload:Multipart] Part ${partInfo.part_number}/${totalParts} uploaded (${unsavedPartsCount} unsaved), etag: ${etag.substring(0, 20)}...`);
          }
        } finally {
          partsMutex.unlock();
        }

        // Update uploaded bytes and clear part progress (outside mutex - read-only)
        const chunkSize = chunk.size;
        partProgress.delete(partInfo.part_number);
        uploadedBytes += chunkSize;

        return newPart;

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        lastUrlError = err instanceof Error ? err : new Error(errorMsg);

        // Check if URL expired
        if (errorMsg.includes('URL_EXPIRED')) {
          // 每个分片只允许刷新一次 URL，刷新后再失败直接算该分片失败
          if (!urlRefreshedForThisPart) {
            urlRefreshedForThisPart = true;
            try {
              await refreshAllPartUrls();
              uploadLog(`[Upload:Multipart] Part ${partInfo.part_number} retrying with refreshed URL...`);
              await new Promise(r => setTimeout(r, 500));
              continue;
            } catch (refreshErr) {
              uploadError('[Upload:Multipart] Failed to refresh URLs:', refreshErr);
              lastUrlError = refreshErr instanceof Error ? refreshErr : new Error(String(refreshErr));
              // fall through to consecutive failure counting
            }
          } else {
            uploadWarn(`[Upload:Multipart] Part ${partInfo.part_number} URL still expired after refresh, counting as failure`);
          }
          // fall through to consecutive failure counting
        }

        // All errors (including URL refresh failures) go through consecutive failure counting
        uploadError(`[Upload:Multipart] Part ${partInfo.part_number}/${totalParts} failed after ${attempt + 1} attempts: ${errorMsg}`);

        // Save progress even on error (thread-safe)
        await partsMutex.lock();
        try {
          saveUploadProgress(fileKey, upload_id, object_key, file.name, file.size, parts, effectiveChunkSize);
        } finally {
          partsMutex.unlock();
        }

        consecutivePartFailures++;
        uploadWarn(`[Upload:Multipart] Consecutive part failures: ${consecutivePartFailures}/${CONSECUTIVE_FAIL_THRESHOLD}`);
        if (consecutivePartFailures >= CONSECUTIVE_FAIL_THRESHOLD) {
          uploadError('[Upload:Multipart] 连续 3 个分片失败，中止直传，切换代理模式');
          throw new Error('DIRECT_MODE_STALLED: consecutive part failures');
        }

        throw lastUrlError;
      }
    }

    throw lastUrlError || new Error('Upload failed after all retry attempts');
  });

  // Upload parts concurrently
  uploadLog(`[Upload:Multipart] Starting concurrent upload of ${uploadTasks.length} parts (max concurrency: ${MAX_CONCURRENT_UPLOADS})`);
  try {
    await uploadPartsConcurrently(uploadTasks, MAX_CONCURRENT_UPLOADS);
  } catch (err) {
    // Propagate errors
    throw err;
  }
  uploadLog(`[Upload:Multipart] All parts uploaded successfully, total: ${parts.length}/${part_urls_mutable.length}`);
  uploadLog(`[Upload:Multipart] Final parts: [${parts.map(p => p.part_number).sort((a,b) => a-b).join(', ')}]`);

  // Wait for MD5 calculation to complete
  let md5Hex = '';
  try {
    md5Hex = await md5Promise;
    uploadLog('[Upload:Multipart:MD5] Final MD5 hash calculated:', md5Hex);
  } catch (err) {
    uploadWarn('[Upload:Multipart:MD5] Failed to calculate final MD5 hash:', err);
  }

  // 合并阶段：显示 95% 进度
  if (onProgress) {
    onProgress({
      loaded: file.size,
      total: file.size,
      percent: 95,
      status: 'merging',
    });
  }

  // Complete multipart upload with retry for 503 errors
  // OBS服务端合并大文件可能耗时较长，添加重试机制提高成功率
  uploadLog('[Upload:Multipart] Starting complete multipart upload (merging parts on OBS server)...');
  const MAX_COMPLETE_RETRIES = 3;
  let completeError: Error | undefined;

  for (let attempt = 0; attempt < MAX_COMPLETE_RETRIES; attempt++) {
    try {
      // Sort parts by part_number (defense in depth - backend also sorts)
      const sortedParts = [...parts].sort((a, b) => a.part_number - b.part_number);

      // Validate parts are complete and consecutive
      const totalParts = Math.ceil(file.size / effectiveChunkSize);
      uploadLog(`[Upload:Multipart:Complete] Validation: file_size=${file.size}, chunk_size=${effectiveChunkSize}, expected_parts=${totalParts}, received_parts=${sortedParts.length}`);

      const missingParts = [];
      for (let i = 1; i <= totalParts; i++) {
        if (!sortedParts.find(p => p.part_number === i)) {
          missingParts.push(i);
        }
      }

      if (missingParts.length > 0) {
        uploadError(
          `[Upload:Multipart:Complete] Missing parts detected: ${missingParts.join(', ')}`
        );
        uploadError(
          `[Upload:Multipart:Complete] Received parts: [${sortedParts.map(p => p.part_number).join(', ')}]`
        );
        throw new Error(
          `上传不完整,缺少 ${missingParts.length} 个分片: ${missingParts.slice(0, 5).join(', ')}${missingParts.length > 5 ? '...' : ''}`
        );
      }

      // Validate ETags
      for (const part of sortedParts) {
        if (!part.etag || part.etag.trim() === '') {
          uploadError(
            `[Upload:Multipart:Complete] Invalid ETag for part ${part.part_number}`
          );
          throw new Error(`分片 ${part.part_number} 的 ETag 无效`);
        }
      }

      uploadLog(
        `[Upload:Multipart:Complete] Sending ${sortedParts.length} parts, ` +
        `range=[${sortedParts[0].part_number}..${sortedParts[sortedParts.length - 1].part_number}]`
      );

      uploadLog(`[Upload:Multipart:Complete] Attempt ${attempt + 1}/${MAX_COMPLETE_RETRIES}`);
      const { file_url } = await completeMultipartUpload(upload_id, object_key, sortedParts);
      uploadLog(`[Upload:Multipart:Complete] Success! File URL: ${file_url}`);

      // ✅ Fix 3: Verify OBS file exists and is complete after multipart merge
      uploadLog(`[Upload:Multipart:Verify] Starting file verification...`);
      try {
        const verifyResult = await verifyFile(object_key, file.size);
        if (!verifyResult.exists) {
          throw new Error(`Upload verification failed: File does not exist in OBS.`);
        }
        // ✅ Protect against undefined size (OBS may not return contentLength)
        if (verifyResult.size !== undefined && verifyResult.size !== file.size) {
          throw new Error(
            `File size mismatch: expected ${file.size} bytes, got ${verifyResult.size} bytes`
          );
        }
        uploadLog(`[Upload:Multipart:Verify] ✅ File verified! Size: ${verifyResult.size} bytes`);
      } catch (verifyErr) {
        uploadError(`[Upload:Multipart:Verify] ❌ Verification failed:`, verifyErr);
        throw verifyErr;
      }

      // Success - clear resume data
      clearUploadProgress(fileKey);

      // Notify backend for upload logging (fire-and-forget)
      const endTime = new Date().toISOString();
      const duration = ((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000).toFixed(2);
      const uploadSummary = {
        file_name: file.name,
        file_size: file.size,
        start_time: startTime,
        end_time: endTime,
        duration: `${duration}s`,
        mode: mode,
        chunk_size: `${effectiveChunkSize / 1024 / 1024}MB`,
        result: 'success' as const,
      };
      uploadLog(`[upload-summary] ${JSON.stringify(uploadSummary)}`);
      notifyUploadComplete(uploadSummary).catch(err => uploadWarn('[Upload] Failed to notify backend:', err));

      // 显式更新进度到 100%，确保 UI 状态正确更新
      if (onProgress) {
        onProgress({
          loaded: file.size,
          total: file.size,
          percent: 100,
        });
      }

      return {
        objectKey: object_key,
        fileUrl: file_url,
        fileName: file.name,
        fileSize: file.size,
        md5: md5Hex,  // Return MD5 in hex format
      };
    } catch (err) {
      completeError = err instanceof Error ? err : new Error(String(err));
      const is503 = completeError.message.includes('503');
      const responseDetail: string = (completeError as any).response?.data?.detail ?? '';
      const isInvalidPart = responseDetail.includes('[InvalidPart]') || responseDetail.includes('could not be found');
      uploadError(`[Upload:Multipart:Complete] Attempt ${attempt + 1}/${MAX_COMPLETE_RETRIES} failed: ${completeError.message}`);

      if (isInvalidPart) {
        uploadError(`[Upload:Multipart:Complete] InvalidPart detected — dumping all ${parts.length} parts sent to complete:`);
        parts.forEach(p => {
          uploadError(`[Upload:Multipart:Complete]   Part ${p.part_number}: etag=${p.etag}`);
        });
      }

      if (attempt < MAX_COMPLETE_RETRIES - 1) {
        // Use exponential backoff for 503 (service unavailable)
        const delay = is503
          ? Math.min(2000 * Math.pow(2, attempt), 30000)  // 2s, 4s, 8s, ...max 30s
          : 5000 * (attempt + 1);  // 5s, 10s, 15s

        uploadWarn(
          `Complete multipart upload failed (attempt ${attempt + 1}/${MAX_COMPLETE_RETRIES}): ${completeError.message}`
        );
        if (is503) {
          uploadWarn(`OBS service unavailable, retrying after ${delay}ms...`);
        }

        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  // All retries failed - enhance error message for 503
  if (completeError) {
    if (completeError.message.includes('503')) {
      const resumableMsg = parts.length > 0
        ? `服务暂时不可用，已保存上传进度(${parts.length}个分片)，请稍后继续上传`
        : '服务暂时不可用，请稍后重试';
      throw new Error(resumableMsg);
    }
    throw completeError;
  }

  throw new Error('Upload failed');  // Should never reach here
};

/**
 * Upload a single part with timeout support.
 * ✅ Supports upload cancellation via uploadId
 */
const uploadPartOnce = (
  url: string,
  chunk: Blob,
  onProgress?: (loaded: number) => void,
  uploadId?: string,  // ✅ NEW: Unique ID for cancellation support
  fileName?: string   // ✅ File name for stall error message
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // ✅ Save xhr reference for potential cancellation
    if (uploadId) {
      const task = activeUploads.get(uploadId);
      if (task) {
        task.xhr = xhr;
        // Check if already aborted before starting
        if (task.aborted) {
          reject(new Error('Upload aborted'));
          return;
        }
      }
    }

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');

    xhr.timeout = PART_UPLOAD_TIMEOUT;

    // Stall detector: abort if upload progress freezes for UPLOAD_STALL_TIMEOUT_MS
    // lastProgressAt is reset right after xhr.send() so the 30s window starts from actual transmission
    let lastProgressAt = Date.now();
    const stallTimer = setInterval(() => {
      if (Date.now() - lastProgressAt > UPLOAD_STALL_TIMEOUT_MS) {
        clearInterval(stallTimer);
        xhr.abort();
        reject(new Error(`UPLOAD_STALLED: no upload progress for 30s [file: ${fileName ?? 'unknown'}]`));
      }
    }, 5_000);

    xhr.upload.onprogress = (e) => {
      lastProgressAt = Date.now();
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded);
      }
    };

    xhr.onload = () => {
      clearInterval(stallTimer);
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');

        // ✅ Fix 1: 增强ETag验证 - ETag必须存在且非空
        if (!etag || etag.trim() === '') {
          uploadError('[Upload:Part:ETag] ❌ OBS returned 200 but no ETag header! File may not have been saved.');
          reject(new Error(
            `Upload verification failed: OBS returned ${xhr.status} but no ETag. ` +
            `This usually means the file was not properly saved. Please retry the upload.`
          ));
          return;
        }

        uploadLog(`[Upload:Part:ETag] ✅ Received valid ETag: ${etag}`);
        resolve(etag);
      } else {
        reject(new Error(`Part upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    };

    xhr.ontimeout = () => {
      clearInterval(stallTimer);
      xhr.abort();
      reject(new Error('Part upload timeout'));
    };
    xhr.onerror = () => {
      clearInterval(stallTimer);
      // CORS errors or network failures trigger onerror
      // For CORS-blocked 404s (expired URLs), xhr.status is typically 0
      const statusHint = xhr.status !== 0 ? ` (status ${xhr.status})` : '';
      reject(new Error(`Part upload failed - network or CORS error${statusHint}`));
    };
    xhr.send(chunk);
    // Reset stall timer baseline to actual send time, so the 30s window starts from transmission
    lastProgressAt = Date.now();
  });
};

/**
 * Check if error is due to expired signed URL (404 or CORS error)
 */
const isUrlExpiredError = (err: Error): boolean => {
  const msg = err.message.toLowerCase();
  return msg.includes('404') ||
         msg.includes('not found') ||
         msg.includes('cors') ||
         msg.includes('access-control-allow-origin');
};

/**
 * Upload a single part of multipart upload with retry.
 * Uses exponential backoff for 503 errors (service unavailable).
 * Detects 404/CORS errors (expired URL) and throws special error for refresh.
 */
const uploadPart = async (
  url: string,
  chunk: Blob,
  onProgress?: (loaded: number) => void,
  expectedMD5Hex?: string,  // ✅ ETag校验用的预期MD5（Hex格式）
  partNumber?: number,  // ✅ Part number for logging
  fileName?: string,  // ✅ File name for error notification
  fileSize?: number   // ✅ File size for error notification
): Promise<string> => {
  let lastError: Error | undefined;
  let md5MismatchCount = 0;  // ✅ Track MD5 mismatch separately

  for (let attempt = 0; attempt < MAX_PART_RETRIES; attempt++) {
    try {
      const etag = await uploadPartOnce(url, chunk, onProgress, undefined, fileName);

      // ✅ 立即记录收到的ETag
      uploadLog(`[Upload:Part:Received] Got ETag from OBS: ${etag.substring(0, 32)}...`);

      // ✅ 然后进行ETag校验
      if (expectedMD5Hex) {
        const etagMD5 = etag.replace(/"/g, '').toLowerCase();
        const expectedMD5 = expectedMD5Hex.toLowerCase();

        if (etagMD5 !== expectedMD5) {
          md5MismatchCount++;
          uploadError(`[Upload:Part:ETag] MD5 mismatch! Expected: ${expectedMD5}, Got: ${etagMD5}`);

          // ✅ 通知后端ETag校验失败，记录重试次数
          if (fileName && fileSize !== undefined) {
            const { notifyETagMismatch } = await import('../api/storage');
            notifyETagMismatch({
              file_name: fileName,
              file_size: fileSize,
              mode: 'Direct',
              part_number: partNumber,
              expected_md5: expectedMD5,
              received_etag: etagMD5,
              timestamp: new Date().toISOString(),
              error_message: 'ETag verification failed after upload'
            }).catch(() => {}); // Ignore notification errors
          }

          // ✅ Fix 5: 允许单次重试，延迟 3-5 秒给 OBS 处理时间
          if (md5MismatchCount <= 1 && attempt < MAX_PART_RETRIES - 1) {
            const delay = 3000 + Math.random() * 2000;  // 3-5s
            uploadWarn(
              `[Upload:Part:ETag] MD5 mismatch (${md5MismatchCount}/1), ` +
              `will retry after ${Math.round(delay)}ms to check if OBS is still processing...`
            );
            await new Promise(r => setTimeout(r, delay));
            continue;  // ✅ 重试
          }

          // ❌ 第二次 mismatch 或重试耗尽，直接失败
          uploadError(`[Upload:Part:ETag] MD5 mismatch persists after ${md5MismatchCount} attempts, data corruption suspected`);
          const error = new Error(
            `分片 ${partNumber || ''} MD5 验证失败，已重试 ${md5MismatchCount} 次。` +
            `预期 MD5=${expectedMD5.substring(0, 16)}...，实际=${etagMD5.substring(0, 16)}...。` +
            `可能原因：网络损坏、文件被修改或存储异常。`
          );
          (error as any).etagMismatch = true;
          (error as any).expectedMD5 = expectedMD5;
          (error as any).receivedETag = etagMD5;
          throw error;
        }

        uploadLog(`[Upload:Part:ETag] ✓ MD5 verified: ${expectedMD5}`);
      }

      return etag;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));

      // ✅ 检查是否是 MD5 mismatch 错误，若是则不继续普通重试
      if ((lastError as any).etagMismatch === true) {
        throw lastError;  // 直接抛出，不继续循环
      }

      uploadWarn(`Part upload attempt ${attempt + 1} failed: ${lastError.message}`);

      // Check if URL expired (404 or CORS error)
      if (isUrlExpiredError(lastError)) {
        uploadError('[Upload:Part] Signed URL expired (404/CORS), need to refresh URLs');
        // Throw special error to trigger URL refresh at higher level
        throw new Error('URL_EXPIRED: ' + lastError.message);
      }

      if (attempt < MAX_PART_RETRIES - 1) {
        // For 503 errors, use exponential backoff (up to 10s)
        // For other errors, use linear backoff
        const is503 = lastError.message.includes('503');
        const delay = is503
          ? Math.min(1000 * Math.pow(2, attempt), 10000)  // Exponential: 1s, 2s, 4s, ...
          : 1000 * (attempt + 1);  // Linear: 1s, 2s, 3s

        if (is503) {
          uploadWarn(`OBS service unavailable (503), retrying after ${delay}ms...`);
        }

        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  throw lastError;
};

// ─── Proxy upload (via backend) ────────────────────────────────────────────

/**
 * Upload a file via backend proxy.
 * Routes to single or multipart proxy based on file size.
 */
const uploadViaProxy = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  config?: UploadConfig,
  uploadKey?: string
): Promise<UploadResult> => {
  uploadLog(`[Upload:Proxy] Starting PROXY upload: ${file.name}, size: ${file.size}`);

  const configuredChunkSize = config?.chunkSize;

  // If user configured chunk size, force multipart upload
  if (configuredChunkSize) {
    uploadLog(`[Upload:Proxy] Using multipart proxy upload with chunk size: ${(configuredChunkSize / 1024 / 1024).toFixed(0)}MB`);
    return uploadMultipartViaProxy(file, onProgress, configuredChunkSize, 'Proxy', uploadKey);
  }

  // Otherwise use file size threshold
  if (file.size > MULTIPART_THRESHOLD) {
    uploadLog(`[Upload:Proxy] Using multipart proxy upload (file > ${MULTIPART_THRESHOLD} bytes)`);
    return uploadMultipartViaProxy(file, onProgress, undefined, 'Proxy', uploadKey);
  }
  uploadLog(`[Upload:Proxy] Using single file proxy upload (file <= ${MULTIPART_THRESHOLD} bytes)`);
  return uploadSingleViaProxy(file, onProgress, uploadKey);
};

/**
 * Single file upload via backend proxy (for files < 100MB) with ETag verification.
 */
const uploadSingleViaProxy = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  uploadKey?: string
): Promise<UploadResult> => {
  // 如果已被用户取消，拒绝启动
  if (uploadKey && cancelledUploadKeys.has(uploadKey)) {
    cancelledUploadKeys.delete(uploadKey);
    throw new Error('Upload cancelled by user');
  }

  // Register AbortController so abortUpload(uploadKey) can cancel proxy requests
  const controller = new AbortController();
  if (uploadKey) {
    const existing = activeUploads.get(uploadKey);
    activeUploads.set(uploadKey, { ...(existing || { file, aborted: false }), controller });
  }

  // Calculate MD5 for single file proxy upload
  uploadLog('[Upload:Proxy:Single:MD5] Calculating file MD5...');
  let md5Hex = '';
  try {
    md5Hex = await calculateFileMD5(file);
    uploadLog('[Upload:Proxy:Single:MD5] MD5 calculated:', md5Hex);
  } catch (err) {
    uploadWarn('[Upload:Proxy:Single:MD5] Failed to calculate MD5:', err);
  }

  let result: Awaited<ReturnType<typeof proxyUpload>>;
  try {
  result = await proxyUpload(
    file,
    file.name,
    file.type || 'application/octet-stream',
    (e) => {
      if (onProgress && e.total) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        });
      }
    },
    md5Hex,  // ✅ 传递MD5 Hex用于ETag校验
    controller.signal
  );
  } finally {
    if (uploadKey) activeUploads.delete(uploadKey);
  }

  // ✅ ETag校验
  if (md5Hex && result.etag) {
    uploadLog(`[Upload:Proxy:Single:Received] Got ETag from server: ${result.etag.substring(0, 32)}...`);

    const etagMD5 = result.etag.replace(/"/g, '').toLowerCase();
    const expectedMD5 = md5Hex.toLowerCase();

    if (etagMD5 !== expectedMD5) {
      uploadError(`[Upload:Proxy:Single:ETag] MD5 mismatch! Expected: ${expectedMD5}, Got: ${etagMD5}`);

      // ✅ 通知后端ETag校验失败
      const { notifyETagMismatch } = await import('../api/storage');
      await notifyETagMismatch({
        file_name: file.name,
        file_size: file.size,
        mode: 'Proxy',
        expected_md5: expectedMD5,
        received_etag: etagMD5,
        timestamp: new Date().toISOString(),
        error_message: 'ETag verification failed for proxy single file upload'
      }).catch(() => {}); // Ignore notification errors

      throw new Error(`File MD5 mismatch after proxy upload`);
    }

    uploadLog(`[Upload:Proxy:Single:ETag] ✓ MD5 verified: ${expectedMD5}`);
  }

  // ✅ Fix 1P: Verify file exists and is accessible in OBS (Proxy mode)
  uploadLog(`[Upload:Proxy:Single:Verify] Starting file verification...`);
  try {
    const verifyResult = await verifyFile(result.object_key, file.size);
    if (!verifyResult.exists) {
      throw new Error(`Upload verification failed: File does not exist in OBS.`);
    }
    // ✅ Protect against undefined size
    if (verifyResult.size !== undefined && verifyResult.size !== file.size) {
      throw new Error(
        `File size mismatch: expected ${file.size} bytes, got ${verifyResult.size} bytes`
      );
    }
    uploadLog(`[Upload:Proxy:Single:Verify] ✅ File verified! Size: ${verifyResult.size} bytes`);
  } catch (verifyErr) {
    uploadError(`[Upload:Proxy:Single:Verify] ❌ Verification failed:`, verifyErr);
    throw verifyErr;
  }

  return {
    objectKey: result.object_key,
    fileUrl: result.file_url,
    fileName: file.name,
    fileSize: file.size,
    md5: md5Hex,  // Return MD5 in hex format
  };
};

/**
 * Multipart upload via backend proxy (for files > 100MB) with resume support.
 * Uses the same init/complete flow but proxies each part through backend.
 */
const uploadMultipartViaProxy = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  chunkSize?: number,  // Optional: allows external override
  mode: string = 'Proxy',  // Upload mode for logging
  uploadKey?: string
): Promise<UploadResult> => {
  const startTime = new Date().toISOString();
  const fileKey = getFileKey(file);

  // 如果已被用户取消，拒绝启动新的上传任务
  if (uploadKey && cancelledUploadKeys.has(uploadKey)) {
    cancelledUploadKeys.delete(uploadKey);
    throw new Error('Upload cancelled by user');
  }

  // Register AbortController so abortUpload(uploadKey) can cancel proxy requests
  const controller = new AbortController();
  if (uploadKey) {
    const existing = activeUploads.get(uploadKey);
    activeUploads.set(uploadKey, { ...(existing || { file, aborted: false }), controller });
  }

  // Start MD5 calculation in parallel (don't wait)
  const md5Promise = calculateFileMD5(file).catch((err) => {
    uploadWarn('[Upload:Proxy:Multipart:MD5] Failed to calculate MD5:', err);
    return '';
  });
  uploadLog('[Upload:Proxy:Multipart:MD5] Started MD5 calculation in background');

  // Check for existing upload progress and determine chunk size
  const resumeData = loadUploadProgress(fileKey);
  let effectiveChunkSize: number;

  if (resumeData && resumeData.fileName === file.name && resumeData.fileSize === file.size) {
    // Check if user changed chunk size - if so, restart upload
    if (chunkSize && resumeData.chunkSize && chunkSize !== resumeData.chunkSize) {
      uploadWarn(
        `[Upload:Proxy:Resume] Chunk size changed from ${resumeData.chunkSize / 1024 / 1024}MB to ${chunkSize / 1024 / 1024}MB, ` +
        `clearing progress and restarting upload`
      );
      clearUploadProgress(fileKey);
      effectiveChunkSize = chunkSize;
    } else {
      // Resume: must use original chunk size
      effectiveChunkSize = resumeData.chunkSize;
      uploadLog(`[Upload:Proxy:Resume] Using saved chunk size: ${effectiveChunkSize / 1024 / 1024}MB`);
    }
  } else {
    effectiveChunkSize = chunkSize!;
    uploadLog(`[Upload:Proxy:New] Using chunk size: ${effectiveChunkSize / 1024 / 1024}MB`);
  }

  let upload_id: string | undefined;
  let object_key: string | undefined;
  let part_urls: Array<{ part_number: number; url: string }> | undefined;
  let parts: Array<{ part_number: number; etag: string }> = [];
  let uploadedBytes = 0;

  if (resumeData && resumeData.fileName === file.name && resumeData.fileSize === file.size) {
    // Resume existing upload
    uploadLog(`Resuming proxy upload for ${file.name}, ${resumeData.completedParts.length} parts already uploaded`);
    upload_id = resumeData.uploadId;
    object_key = resumeData.objectKey;
    parts = [...resumeData.completedParts];

    // Calculate already uploaded bytes
    uploadedBytes = resumeData.completedParts.reduce((sum, part) => {
      const start = (part.part_number - 1) * effectiveChunkSize;
      const end = Math.min(start + effectiveChunkSize, file.size);
      return sum + (end - start);
    }, 0);

    // Refresh signed URLs for existing upload session (without creating new session)
    let proxyRefreshResult: Awaited<ReturnType<typeof refreshMultipartUrls>>;
    try {
      proxyRefreshResult = await refreshMultipartUrls(
        upload_id,
        object_key,
        file.size,
        effectiveChunkSize
      );
    } catch (refreshErr) {
      const refreshStatus = (refreshErr as any)?.response?.status;
      const refreshErrorCode = (refreshErr as any)?.response?.data?.error;
      if (refreshStatus === 409 && refreshErrorCode === 'upload_already_completed') {
        uploadWarn('[Upload:Proxy:Multipart] upload_id already completed/aborted, clearing cache and restarting');
        clearUploadProgress(fileKey);
        throw new Error('UPLOAD_SESSION_EXPIRED: upload_id no longer valid, please retry');
      }
      throw refreshErr;
    }
    part_urls = proxyRefreshResult.part_urls;

    // Show initial progress
    if (onProgress) {
      const percent = Math.min(Math.round((uploadedBytes / file.size) * 95), 95);
      onProgress({
        loaded: uploadedBytes,
        total: file.size,
        percent,
      });
    }
  } else {
    // Start new upload
    const expectedParts = Math.ceil(file.size / effectiveChunkSize);
    uploadLog(`[Upload:Proxy:Multipart] Initializing new multipart upload (expected ${expectedParts} parts for ${file.size} bytes)...`);
    const result = await initMultipartUpload(
      file.name,
      file.type || 'application/octet-stream',
      file.size,
      effectiveChunkSize
    );
    upload_id = result.upload_id;
    object_key = result.object_key;
    part_urls = result.part_urls;
    uploadLog(`[Upload:Proxy:Multipart] Initialized: upload_id=${upload_id.substring(0, 20)}..., object_key=${object_key}, parts=${part_urls.length}`);

    if (part_urls.length !== expectedParts) {
      uploadWarn(`[Upload:Proxy:Multipart] Warning: Backend returned ${part_urls.length} part URLs, but expected ${expectedParts} parts!`);
    }
  }

  // Ensure all variables are initialized
  if (!upload_id || !object_key || !part_urls) {
    throw new Error('[Upload:Proxy:Multipart] Failed to initialize upload: missing upload_id, object_key, or part_urls');
  }

  // Mutable reference so URL refresh can update the array in-place (same pattern as Direct mode)
  let part_urls_mutable = part_urls;

  // Global URL refresh lock — same guard as Direct mode
  let urlRefreshPromise: Promise<void> | null = null;
  let urlRefreshCount = 0;
  const MAX_URL_REFRESH = 3;

  const refreshAllProxyPartUrls = async (): Promise<void> => {
    if (urlRefreshPromise) {
      uploadLog('[Upload:Proxy:Multipart:URLRefresh] Already refreshing, waiting...');
      return urlRefreshPromise;
    }
    if (urlRefreshCount >= MAX_URL_REFRESH) {
      throw new Error(`URL refresh limit reached (${MAX_URL_REFRESH} attempts)`);
    }
    urlRefreshCount++;
    uploadLog(`[Upload:Proxy:Multipart:URLRefresh] Starting global URL refresh (attempt ${urlRefreshCount}/${MAX_URL_REFRESH})...`);
    urlRefreshPromise = (async () => {
      try {
        const refreshResult = await refreshMultipartUrls(upload_id, object_key, file.size, effectiveChunkSize);
        part_urls_mutable = refreshResult.part_urls;
        uploadLog(`[Upload:Proxy:Multipart:URLRefresh] Successfully refreshed ${part_urls_mutable.length} part URLs`);
      } finally {
        urlRefreshPromise = null;
      }
    })();
    return urlRefreshPromise;
  };

  // Upload each part via proxy with concurrency (占总进度的 0-95%)
  const completedPartNumbers = new Set(parts.map(p => p.part_number));
  const remainingParts = part_urls_mutable.filter(p => !completedPartNumbers.has(p.part_number));

  uploadLog(`[Upload:Proxy:Multipart] Part URLs received: ${part_urls_mutable.length}`);
  uploadLog(`[Upload:Proxy:Multipart] Already completed: ${parts.length} parts [${Array.from(completedPartNumbers).sort((a,b) => a-b).join(', ')}]`);
  uploadLog(`[Upload:Proxy:Multipart] Starting to upload ${remainingParts.length} remaining parts (${parts.length} already completed)`);

  // Track progress for each part being uploaded
  const partProgress = new Map<number, number>();
  let lastReportedPercent = Math.min(Math.round((uploadedBytes / file.size) * 95), 95);
  const partsMutex = new AsyncMutex();
  let unsavedPartsCount = 0; // Batch counter for localStorage writes

  // Speed tracking
  const uploadStartTime = Date.now();
  const startBytes = uploadedBytes;
  let lastSpeedLogTime = uploadStartTime;
  let lastSpeedLogBytes = startBytes;
  let lastCurrentSpeed = 0; // Track most recent current speed

  const updateProgress = () => {
    if (!onProgress) return;

    const currentProgress = uploadedBytes + Array.from(partProgress.values()).reduce((sum, p) => sum + p, 0);
    const percent = Math.min(Math.round((currentProgress / file.size) * 95), 95);

    // Calculate speed (bytes per second)
    const now = Date.now();
    const elapsedSeconds = (now - uploadStartTime) / 1000;
    const uploadedSinceStart = currentProgress - startBytes;
    const speed = elapsedSeconds > 0 ? uploadedSinceStart / elapsedSeconds : 0;
    const remainingBytes = file.size - currentProgress;
    const remainingTime = speed > 0 ? remainingBytes / speed : 0;

    // Calculate current speed (real-time, based on recent data)
    const recentBytes = currentProgress - lastSpeedLogBytes;
    const recentSeconds = (now - lastSpeedLogTime) / 1000;
    const currentSpeed = recentSeconds > 0 ? recentBytes / recentSeconds : 0;

    // Log speed every 5 seconds
    if (now - lastSpeedLogTime >= 5000) {
      lastCurrentSpeed = currentSpeed; // Update tracked current speed
      const speedMBps = (currentSpeed / 1024 / 1024).toFixed(2);
      const avgSpeedMBps = (speed / 1024 / 1024).toFixed(2);
      const eta = remainingTime > 60 ? `${Math.round(remainingTime / 60)}min` : `${Math.round(remainingTime)}s`;
      uploadLog(`[Upload:Proxy:Speed] ${percent}% | Current: ${speedMBps} MB/s | Avg: ${avgSpeedMBps} MB/s | ETA: ${eta}`);
      lastSpeedLogTime = now;
      lastSpeedLogBytes = currentProgress;
    }

    // Only update if percent changed to reduce UI updates
    if (percent > lastReportedPercent) {
      lastReportedPercent = percent;
      onProgress({
        loaded: currentProgress,
        total: file.size,
        percent,
        speed,
        currentSpeed: lastCurrentSpeed > 0 ? lastCurrentSpeed : speed, // Use last current speed or fallback to average
        remainingTime,
      });
    }
  };

  // Create upload tasks for remaining parts
  const uploadTasks = remainingParts.map(partInfo => async () => {
    const start = (partInfo.part_number - 1) * effectiveChunkSize;
    const end = Math.min(start + effectiveChunkSize, file.size);
    const chunk = file.slice(start, end);
    const partSize = (chunk.size / 1024 / 1024).toFixed(2);

    // ✅ 计算分片MD5用于ETag校验
    let chunkMD5Hex = '';
    try {
      const chunkArrayBuffer = await chunk.arrayBuffer();
      const chunkSpark = new SparkMD5.ArrayBuffer();
      chunkSpark.append(chunkArrayBuffer);
      chunkMD5Hex = chunkSpark.end();
      uploadLog(`[Upload:Proxy:Part:MD5] Part ${partInfo.part_number} MD5: ${chunkMD5Hex}`);
    } catch (err) {
      uploadWarn(`[Upload:Proxy:Part:MD5] Failed to calculate MD5 for part ${partInfo.part_number}:`, err);
      // MD5计算失败不阻塞上传，继续不带MD5校验上传
    }

    // Log upload start (proxy mode)
    uploadLog(`[Upload:Proxy:Part:${partInfo.part_number}/${part_urls_mutable.length}] Starting proxy upload (${partSize} MB)...`);
    const uploadStartTime = Date.now();

    // ✅ 添加重试循环
    let lastError: Error | undefined;
    let md5MismatchCount = 0;  // ✅ Track MD5 mismatches across all attempts
    let urlRefreshedForThisPart = false;  // Each part may trigger at most one URL refresh

    for (let attempt = 0; attempt < MAX_PROXY_PART_RETRIES; attempt++) {
      // 检查是否已被取消
      if (controller.signal.aborted) {
        throw new Error('Upload aborted by user');
      }
      try {
        // Always look up the URL from the mutable array so URL refreshes are picked up
        const currentPartInfo = part_urls_mutable.find(p => p.part_number === partInfo.part_number);
        if (!currentPartInfo) {
          throw new Error(`Part ${partInfo.part_number} not found in part_urls after refresh`);
        }
        const { etag } = await proxyUploadPart(currentPartInfo.url, chunk, (e) => {
          if (e.total) {
            partProgress.set(partInfo.part_number, e.loaded);
            updateProgress();
          }
        }, chunkMD5Hex, attempt + 1, part_urls_mutable.length, controller.signal, uploadKey);

        // ✅ 立即记录收到的ETag
        uploadLog(`[Upload:Proxy:Part:${partInfo.part_number}] Received ETag from server: ${etag.substring(0, 32)}...`);

        // ✅ 然后进行ETag校验
        if (chunkMD5Hex) {
          const etagMD5 = etag.replace(/"/g, '').toLowerCase();
          const expectedMD5 = chunkMD5Hex.toLowerCase();

          if (etagMD5 !== expectedMD5) {
            uploadError(`[Upload:Proxy:Part:ETag] MD5 mismatch! Part ${partInfo.part_number}: Expected: ${expectedMD5}, Got: ${etagMD5}`);

            // ✅ 通知后端ETag校验失败
            const { notifyETagMismatch } = await import('../api/storage');
            await notifyETagMismatch({
              file_name: file.name,
              file_size: file.size,
              mode: 'Proxy',
              part_number: partInfo.part_number,
              expected_md5: expectedMD5,
              received_etag: etagMD5,
              timestamp: new Date().toISOString(),
              error_message: 'ETag verification failed for proxy multipart upload'
            }).catch(() => {}); // Ignore notification errors

            throw new Error(`Part ${partInfo.part_number} MD5 mismatch`);
          }

          uploadLog(`[Upload:Proxy:Part:ETag] ✓ Part ${partInfo.part_number} MD5 verified: ${expectedMD5}`);
        }

        // ✅ 成功上传，保存进度
        const newPart = { part_number: partInfo.part_number, etag };

        // Thread-safe parts array update
        await partsMutex.lock();
        try {
          parts.push(newPart);
          unsavedPartsCount++;

          // Batch save: save every N parts or on last part
          const totalParts = Math.ceil(file.size / effectiveChunkSize);
          const isLastPart = parts.length === totalParts;
          const shouldSave = unsavedPartsCount >= SAVE_PROGRESS_BATCH_SIZE || isLastPart;

          if (shouldSave) {
            saveUploadProgress(fileKey, upload_id, object_key, file.name, file.size, parts, effectiveChunkSize);
            unsavedPartsCount = 0;
          }
        } finally {
          partsMutex.unlock();
        }

        // Update uploaded bytes and clear part progress (outside mutex - read-only)
        const chunkSize = chunk.size;
        partProgress.delete(partInfo.part_number);
        uploadedBytes += chunkSize;

        // Log upload success with performance metrics (proxy mode)
        const duration = ((Date.now() - uploadStartTime) / 1000).toFixed(1);
        const speed = ((chunk.size / 1024 / 1024) / parseFloat(duration)).toFixed(2);
        uploadLog(`[Upload:Proxy:Part:${partInfo.part_number}] ✓ Complete in ${duration}s (${speed} MB/s), ETag: ${etag.substring(0, 20)}...`);

        return newPart;  // ✅ 成功，跳出重试循环

      } catch (err) {
        lastError = err as Error;
        const status = (err as any)?.response?.status;
        const errorMsg = (err as Error)?.message || '';

        // 409 means the multipart upload was already finalized (late retry after complete).
        // Silently discard — the file is already on OBS.
        if (status === 409) {
          const responseError = (err as any)?.response?.data?.error;
          if (responseError === 'upload_already_completed') {
            uploadLog(
              `[Upload:Proxy:Part:${partInfo.part_number}] Upload already completed (late retry), ignoring`
            );
            return { part_number: partInfo.part_number, etag: '' };
          }
        }

        // ✅ Fix 5: Check if this is MD5 mismatch error（Proxy 模式特殊处理）
        const isMD5Error = errorMsg.toLowerCase().includes('md5 mismatch');

        if (isMD5Error) {
          // ✅ MD5 mismatch 允许单次重试，但不与普通网络错误混淆
          md5MismatchCount++;

          if (md5MismatchCount <= 1 && attempt < MAX_PROXY_PART_RETRIES - 1) {
            const delay = 3000 + Math.random() * 2000;  // 3-5s
            uploadWarn(
              `[Upload:Proxy:Part:${partInfo.part_number}] MD5 mismatch on attempt ${attempt + 1}, ` +
              `will retry after ${Math.round(delay)}ms...`
            );
            await new Promise(r => setTimeout(r, delay));
            continue;  // ✅ 重试
          } else {
            uploadError(`[Upload:Proxy:Part:${partInfo.part_number}] MD5 mismatch persists, aborting`);
            throw new Error(
              `分片 ${partInfo.part_number} MD5 验证失败，已重试 ${md5MismatchCount} 次。` +
              `可能原因：网络损坏、文件被修改或存储异常。`
            );
          }
        }

        // ✅ 判断是否应该重试（排除 MD5 错误）
        // 断流（UPLOAD_STALLED）不原地重试：链路已经停了，原地重试只会在同一根断线上
        // 再挂 3 次。交给调度池按"连续传输错误"熔断整个文件，让用户尽快看到错误。
        const shouldRetry = (
          !isMD5Error &&  // ✅ MD5 错误已单独处理，不进入普通重试
          !isStallError(errorMsg) &&
          (status === 403 ||  // 可能是速率限制
          status === 429 ||  // Too Many Requests
          status === 503 ||  // Service Unavailable
          status === 504 ||  // Gateway Timeout
          !status)           // 网络错误（无status）
        );

        if (shouldRetry && attempt < MAX_PROXY_PART_RETRIES - 1) {
          // 403 means the presigned URL may be expired — refresh once before retrying
          if (status === 403 && !urlRefreshedForThisPart) {
            urlRefreshedForThisPart = true;
            uploadWarn(
              `[Upload:Proxy:Part:${partInfo.part_number}] ⚠️ Attempt ${attempt + 1}/${MAX_PROXY_PART_RETRIES} failed ` +
              `(status=403), refreshing URLs before retry...`
            );
            try {
              await refreshAllProxyPartUrls();
            } catch (refreshErr) {
              uploadError('[Upload:Proxy:Multipart:URLRefresh] Failed to refresh URLs:', refreshErr);
              // Continue with next attempt using old URL (will likely fail again but exhaust retries cleanly)
            }
            continue;  // 立即重试，不额外等待
          }

          // ✅ 计算退避延迟
          const is5xx = status && status >= 500;
          const delay = is5xx
            ? Math.min(2000 * Math.pow(2, attempt), 10000)  // 指数退避：2s, 4s, 8s
            : 1000 * (attempt + 1);                         // 线性退避：1s, 2s, 3s

          uploadWarn(
            `[Upload:Proxy:Part:${partInfo.part_number}] ⚠️ Attempt ${attempt + 1}/${MAX_PROXY_PART_RETRIES} failed ` +
            `(status=${status || 'network_error'}), retrying in ${delay}ms...`
          );

          await new Promise(r => setTimeout(r, delay));
          continue;  // ✅ 重试
        } else {
          // ❌ 不应重试的错误（如MD5 mismatch 超过限制）或重试耗尽
          const duration = ((Date.now() - uploadStartTime) / 1000).toFixed(1);
          const currentProgress = partProgress.get(partInfo.part_number) || 0;
          const progressPercent = chunk.size > 0 ? ((currentProgress / chunk.size) * 100).toFixed(1) : '0';

          uploadError(
            `[Upload:Proxy:Part:${partInfo.part_number}] ✗ Failed after ${attempt + 1} attempts (${duration}s)`,
            {
              error: err instanceof Error ? err.message : String(err),
              status: status || 'network_error',
              partSize: `${partSize} MB`,
              uploadedProgress: `${progressPercent}%`,
              uploadedBytes: currentProgress,
            }
          );

          if (isStallError(errorMsg)) {
            reportUploadEvent('upload_stalled', {
              level: 'error',
              file_name: file.name,
              detail: {
                part_number: partInfo.part_number,
                total_parts: part_urls_mutable.length,
                uploaded_bytes: currentProgress,
                part_size: chunk.size,
                uploaded_progress_percent: Number(progressPercent),
                error: errorMsg,
              },
            }).catch(() => {});
          }

          break;  // 跳出重试循环
        }
      }
    }

    // ❌ 重试耗尽，保存进度并抛出错误
    await partsMutex.lock();
    try {
      saveUploadProgress(fileKey, upload_id, object_key, file.name, file.size, parts, effectiveChunkSize);
    } finally {
      partsMutex.unlock();
    }

    throw lastError || new Error(`Part ${partInfo.part_number} upload failed`);
  });

  // Upload parts concurrently
  uploadLog(`[Upload:Multipart] Starting concurrent upload of ${uploadTasks.length} parts (max concurrency: ${MAX_CONCURRENT_UPLOADS})`);
  try {
    await uploadPartsConcurrently(uploadTasks, MAX_CONCURRENT_UPLOADS);
  } catch (concurrentErr) {
    // Save whatever parts succeeded so the next retry resumes from the correct checkpoint
    await partsMutex.lock();
    try {
      saveUploadProgress(fileKey, upload_id, object_key, file.name, file.size, parts, effectiveChunkSize);
    } finally {
      partsMutex.unlock();
    }
    throw concurrentErr;
  }
  uploadLog(`[Upload:Multipart] All parts uploaded successfully, total: ${parts.length}/${part_urls_mutable.length}`);
  uploadLog(`[Upload:Multipart] Final parts: [${parts.map(p => p.part_number).sort((a,b) => a-b).join(', ')}]`);

  // Wait for MD5 calculation to complete
  let md5Hex = '';
  try {
    md5Hex = await md5Promise;
    uploadLog('[Upload:Proxy:Multipart:MD5] Final MD5 hash calculated:', md5Hex);
  } catch (err) {
    uploadWarn('[Upload:Proxy:Multipart:MD5] Failed to calculate final MD5 hash:', err);
  }

  // 合并阶段：显示 95% 进度
  if (onProgress) {
    onProgress({
      loaded: file.size,
      total: file.size,
      percent: 95,
      status: 'merging',
    });
  }

  // Complete multipart upload with retry for 503 errors
  // OBS服务端合并大文件可能耗时较长，添加重试机制提高成功率
  uploadLog('[Upload:Multipart] Starting complete multipart upload (merging parts on OBS server)...');
  const MAX_COMPLETE_RETRIES = 3;
  let completeError: Error | undefined;

  for (let attempt = 0; attempt < MAX_COMPLETE_RETRIES; attempt++) {
    try {
      // Sort parts by part_number (defense in depth - backend also sorts)
      const sortedParts = [...parts].sort((a, b) => a.part_number - b.part_number);

      // Validate parts are complete and consecutive
      const totalParts = Math.ceil(file.size / effectiveChunkSize);
      uploadLog(`[Upload:Multipart:Complete] Validation: file_size=${file.size}, chunk_size=${effectiveChunkSize}, expected_parts=${totalParts}, received_parts=${sortedParts.length}`);

      const missingParts = [];
      for (let i = 1; i <= totalParts; i++) {
        if (!sortedParts.find(p => p.part_number === i)) {
          missingParts.push(i);
        }
      }

      if (missingParts.length > 0) {
        uploadError(
          `[Upload:Multipart:Complete] Missing parts detected: ${missingParts.join(', ')}`
        );
        uploadError(
          `[Upload:Multipart:Complete] Received parts: [${sortedParts.map(p => p.part_number).join(', ')}]`
        );
        throw new Error(
          `上传不完整,缺少 ${missingParts.length} 个分片: ${missingParts.slice(0, 5).join(', ')}${missingParts.length > 5 ? '...' : ''}`
        );
      }

      // Validate ETags
      for (const part of sortedParts) {
        if (!part.etag || part.etag.trim() === '') {
          uploadError(
            `[Upload:Multipart:Complete] Invalid ETag for part ${part.part_number}`
          );
          throw new Error(`分片 ${part.part_number} 的 ETag 无效`);
        }
      }

      uploadLog(
        `[Upload:Multipart:Complete] Sending ${sortedParts.length} parts, ` +
        `range=[${sortedParts[0].part_number}..${sortedParts[sortedParts.length - 1].part_number}]`
      );

      uploadLog(`[Upload:Multipart:Complete] Attempt ${attempt + 1}/${MAX_COMPLETE_RETRIES}`);
      const { file_url } = await completeMultipartUpload(upload_id, object_key, sortedParts);
      uploadLog(`[Upload:Multipart:Complete] Success! File URL: ${file_url}`);

      // ✅ Fix 3: Verify OBS file exists and is complete after multipart merge
      uploadLog(`[Upload:Multipart:Verify] Starting file verification...`);
      try {
        const verifyResult = await verifyFile(object_key, file.size);
        if (!verifyResult.exists) {
          throw new Error(`Upload verification failed: File does not exist in OBS.`);
        }
        // ✅ Protect against undefined size (OBS may not return contentLength)
        if (verifyResult.size !== undefined && verifyResult.size !== file.size) {
          throw new Error(
            `File size mismatch: expected ${file.size} bytes, got ${verifyResult.size} bytes`
          );
        }
        uploadLog(`[Upload:Multipart:Verify] ✅ File verified! Size: ${verifyResult.size} bytes`);
      } catch (verifyErr) {
        uploadError(`[Upload:Multipart:Verify] ❌ Verification failed:`, verifyErr);
        throw verifyErr;
      }

      // Success - clear resume data
      clearUploadProgress(fileKey);

      // Notify backend for upload logging (fire-and-forget)
      const endTime = new Date().toISOString();
      const duration = ((new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000).toFixed(2);
      const uploadSummary = {
        file_name: file.name,
        file_size: file.size,
        start_time: startTime,
        end_time: endTime,
        duration: `${duration}s`,
        mode: mode,
        chunk_size: `${effectiveChunkSize / 1024 / 1024}MB`,
        result: 'success' as const,
      };
      uploadLog(`[upload-summary] ${JSON.stringify(uploadSummary)}`);
      notifyUploadComplete(uploadSummary).catch(err => uploadWarn('[Upload] Failed to notify backend:', err));

      // 显式更新进度到 100%，确保 UI 状态正确更新
      if (onProgress) {
        onProgress({
          loaded: file.size,
          total: file.size,
          percent: 100,
        });
      }

      return {
        objectKey: object_key,
        fileUrl: file_url,
        fileName: file.name,
        fileSize: file.size,
        md5: md5Hex,  // Return MD5 in hex format
      };
    } catch (err) {
      completeError = err instanceof Error ? err : new Error(String(err));
      const is503 = completeError.message.includes('503');
      const responseDetail: string = (completeError as any).response?.data?.detail ?? '';
      const isInvalidPart = responseDetail.includes('[InvalidPart]') || responseDetail.includes('could not be found');
      uploadError(`[Upload:Multipart:Complete] Attempt ${attempt + 1}/${MAX_COMPLETE_RETRIES} failed: ${completeError.message}`);

      if (isInvalidPart) {
        uploadError(`[Upload:Multipart:Complete] InvalidPart detected — dumping all ${parts.length} parts sent to complete:`);
        parts.forEach(p => {
          uploadError(`[Upload:Multipart:Complete]   Part ${p.part_number}: etag=${p.etag}`);
        });
      }

      if (attempt < MAX_COMPLETE_RETRIES - 1) {
        // Use exponential backoff for 503 (service unavailable)
        const delay = is503
          ? Math.min(2000 * Math.pow(2, attempt), 30000)  // 2s, 4s, 8s, ...max 30s
          : 5000 * (attempt + 1);  // 5s, 10s, 15s

        uploadWarn(
          `Complete multipart upload failed (attempt ${attempt + 1}/${MAX_COMPLETE_RETRIES}): ${completeError.message}`
        );
        if (is503) {
          uploadWarn(`OBS service unavailable, retrying after ${delay}ms...`);
        }

        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  // All retries failed - clean up and throw
  if (uploadKey) activeUploads.delete(uploadKey);
  if (completeError) {
    if (completeError.message.includes('503')) {
      const resumableMsg = parts.length > 0
        ? `服务暂时不可用，已保存上传进度(${parts.length}个分片)，请稍后继续上传`
        : '服务暂时不可用，请稍后重试';
      throw new Error(resumableMsg);
    }
    throw completeError;
  }

  throw new Error('Upload failed');  // Should never reach here
};

/**
 * Download a file from OBS.
 */
export const downloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
  const { url } = await getDownloadUrl(fileUrl, fileName);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get a shareable download link for a file, following the same internal/external
 * branching as downloadFile, without triggering a browser download.
 */
export const getShareableDownloadLink = async (fileUrl: string, fileName: string): Promise<string> => {
  const { url } = await getDownloadUrl(fileUrl, fileName);
  return url;
};

/**
 * ✅ Abort a specific upload task by uploadId
 * Returns true if the upload was found and aborted, false otherwise
 */
export const abortUpload = (uploadId: string): boolean => {
  // Mark as cancelled so retry loops refuse to start new attempts
  cancelledUploadKeys.add(uploadId);

  const task = activeUploads.get(uploadId);
  if (task) {
    task.aborted = true;
    if (task.xhr) {
      try {
        task.xhr.abort();
        uploadLog(`[Upload:Abort] Aborted upload: ${uploadId}`);
      } catch (err) {
        uploadWarn(`[Upload:Abort] Failed to abort xhr:`, err);
      }
    }
    if (task.controller) {
      try {
        task.controller.abort();
        uploadLog(`[Upload:Abort] Aborted proxy upload: ${uploadId}`);
      } catch (err) {
        uploadWarn(`[Upload:Abort] Failed to abort controller:`, err);
      }
    }
    activeUploads.delete(uploadId);
  }

  return true;
};

/**
 * ✅ Abort all active uploads
 * Returns the number of uploads that were aborted
 */
export const abortAllUploads = (): number => {
  let count = 0;
  for (const [uploadId, task] of activeUploads.entries()) {
    cancelledUploadKeys.add(uploadId);
    task.aborted = true;
    if (task.xhr) {
      try {
        task.xhr.abort();
        count++;
      } catch (err) {
        uploadWarn(`[Upload:Abort] Failed to abort ${uploadId}:`, err);
      }
    }
    if (task.controller) {
      try {
        task.controller.abort();
        count++;
      } catch (err) {
        uploadWarn(`[Upload:Abort] Failed to abort controller for ${uploadId}:`, err);
      }
    }
  }
  activeUploads.clear();
  uploadLog(`[Upload:Abort] Aborted ${count} uploads`);
  return count;
};

/**
 * ✅ Get the count of currently active uploads
 */
export const getActiveUploadCount = (): number => {
  return activeUploads.size;
};

// Expose utilities to window for debugging (development only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).obsDebug = {
    backgroundProbeObs,
    clearProbeCache,
    clearAllObsCache,
    getUploadConfig,
    resetObsDirectMode,
    isObsDirectModeDisabled,
    cleanupExpiredUploads,
  };
  uploadLog('[OBS] Debug utilities available: window.obsDebug');
  uploadLog('[OBS] Quick cache clear: window.obsDebug.clearAllObsCache()');
}
